/*
@license
  Be Yours by roartheme (https://roartheme.co)
  Access unminified JS in assets/global.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://roartheme.co/blogs/beyours/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/
window.theme = window.theme || {};

theme.config = {
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 749px)',
  isTouch: ('ontouchstart' in window) || window.DocumentTouch && window.document instanceof DocumentTouch || window.navigator.maxTouchPoints || window.navigator.msMaxTouchPoints ? true : false,
  rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false
};

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

theme.Currency = (function() {
  const moneyFormat = '${{amount}}'; // eslint-disable-line camelcase

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || moneyFormat;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands
      );
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  function getBaseUnit(variant) {
    if (!variant) {
      return;
    }

    if (!variant.unit_price_measurement || !variant.unit_price_measurement.reference_value) {
      return;
    }

    return variant.unit_price_measurement.reference_value === 1
      ? variant.unit_price_measurement.reference_unit
      : variant.unit_price_measurement.reference_value +
          variant.unit_price_measurement.reference_unit;
  }

  return {
    formatMoney: formatMoney,
    getBaseUnit: getBaseUnit
  };
})();

// Init section function when it's visible, then disable observer
theme.initWhenVisible = function(options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (typeof options.callback === 'function') {
          options.callback();
          observer.unobserve(entry.target);
        }
      }
    });
  }, {rootMargin: `0px 0px ${threshold}px 0px`});

  observer.observe(options.element);
};

(function() {
  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (e) => {
    if (e.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    }
    else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  }

  // Page transition
  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a');
    if (anchor?.href && /^(mailto:|tel:)/.test(anchor.href)) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    } else {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    }
  });
  const beforeUnloadHandler = (event) => {
    document.body.classList.add('unloading');
  }
  window.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('loaded');

    document.dispatchEvent(new CustomEvent('page:loaded'));
  });
  window.addEventListener('pageshow', (event) => {
    // Removes unload class when returning to page via history
    if (event.persisted) {
      document.body.classList.remove('unloading');
    }
  });

  // Viewport height
  function viewportHeight() {
    if (window.matchMedia('(max-width: 990px)')) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    setHeaderBottomPosition();
  }
  window.addEventListener('resize', viewportHeight);
  window.addEventListener('DOMContentLoaded', viewportHeight);

  if (document.body.getAttribute('data-page-rendering') !== null) {
    // Improve initial load time by skipping the rendering of offscreen content
    const observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          const el = entry.target;
          // Not currently in intersection area.
          if (entry.intersectionRatio == 0) {
            return;
          }
          // Trigger rendering for elements within scroll area that haven't already been marked.
          if (!el.markedVisible) {
            el.style.setProperty('content-visibility', 'visible');
            el.markedVisible = true;
          }
        });
      },
      // Set a rendering 50px above and 100px below the main scroll area.
      { rootMargin: '50px 0px 100px 0px' }
    );
    const sections = document.querySelectorAll('.shopify-section + .shopify-section');
    sections.forEach((section) => { observer.observe(section); });
  }

  /*
  // Delay JavaScript until user interaction
  const loadScriptsTimer = setTimeout(loadScripts, 5000);
  const userInteractionEvents = ["mouseover", "mousemove", "keydown", "touchstart", "touchend", "touchmove", "wheel"];
  userInteractionEvents.forEach(function(event) {
    window.addEventListener(event, triggerScriptLoader, {
      passive: !0
    });
  });
  function triggerScriptLoader() {
    loadScripts();
    clearTimeout(loadScriptsTimer);
    userInteractionEvents.forEach(function(event) {
      window.removeEventListener(event, triggerScriptLoader, {
        passive: !0
      });
    });
  }
  function loadScripts() {
    document.querySelectorAll("script[data-type='lazy']").forEach(function(script) {
      script.setAttribute("src", script.getAttribute("data-src"));
    });
  }
  */
})();

// Prevent vertical scroll while using flickity sliders
(function() {
  var e = !1;
  var t;

  document.body.addEventListener('touchstart', function(i) {
    if (!i.target.closest('.flickity-slider')) {
      return e = !1;
    }
    e = !0;
    t = {
      x: i.touches[0].pageX,
      y: i.touches[0].pageY
    }
  })

  document.body.addEventListener('touchmove', function(i) {
    if (e && i.cancelable) {
      var n = {
        x: i.touches[0].pageX - t.x,
        y: i.touches[0].pageY - t.y
      };
      Math.abs(n.x) > Flickity.defaults.dragThreshold && i.preventDefault()
    }
  }, { passive: !1 })
})();

function isStorageSupported (type) {
  // Return false if we are in an iframe without access to sessionStorage
  if (window.self !== window.top) {
    return false;
  }

  const testKey = 'beyours:test';
  let storage;
  if (type === 'session') {
    storage = window.sessionStorage;
  }
  if (type === 'local') {
    storage = window.localStorage;
  }

  try {
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return true;
  }
  catch (error) {
    // Do nothing, this may happen in Safari in incognito mode
    return false;
  }
}

function checkScrollbar() {
  const rect = document.body.getBoundingClientRect();
  return Math.round(rect.left + rect.right) < window.innerWidth;
}

function setScrollbarWidth() {
  if (checkScrollbar()) {
    const element = document.createElement('span');
    element.className = 'modal-scrollbar-measure', document.body.appendChild(element);
  
    const width = element.getBoundingClientRect().width - element.clientWidth;
    document.body.removeChild(element);
    document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
  }
}

function setHeaderBottomPosition() {
  const header = document.querySelector('header-group');

  if (header) {
    const headerBottomPosition = parseInt(header.offsetHeight);

    document.documentElement.style.setProperty('--header-bottom-position', `${ headerBottomPosition }px`);
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
    if (charCode !== 'TAB') return; // If not TAB key
    
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();

  if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

function pauseAllMedia() {
  document.querySelectorAll('iframe.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('iframe.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video-section[data-type="youtube"]:not([data-video-hero]) iframe').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('video-section[data-type="vimeo"]:not([data-video-hero]) iframe').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => {
    if (!video.closest('[data-video-hero]') && !video.closest('.ugc-card')) video.pause();
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
  if (charCode !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

function filterShopifyEvent(event, domElement, callback) {
  let executeCallback = false;
  if (event.type.includes('shopify:section')) {
    if (domElement.hasAttribute('data-section-id') && domElement.getAttribute('data-section-id') === event.detail.sectionId) {
      executeCallback = true;
    }
  }
  else if (event.type.includes('shopify:block') && event.target === domElement) {
    executeCallback = true;
  }
  if (executeCallback) {
    callback(event);
  }
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

function dimissPromotion() {
  return;

  // Discarding this feature because of unintended behavior (customers "accidentally" dismissing promotions)
  const promoQuickViewDrawer = document.querySelector('promo-quick-view-drawer');
        
  if(promoQuickViewDrawer?.querySelector('details[open]')) {
    document.dispatchEvent(new CustomEvent('promo:dismissed', {
      detail: {
        id: promoQuickViewDrawer.getAttribute('data-promo-id')
      }
    }));
  };
}

window.addEventListener('keyup', (event) => {
  if( ! event.code) return;

  const code = event.code.toUpperCase();

  if(code !== 'ESCAPE') return;
  dimissPromotion();
})

const serializeForm = form => {
  const obj = {};
  const formData = new FormData(form);

  for (const key of formData.keys()) {
    const regex = /(?:^(properties\[))(.*?)(?:\]$)/;

    if (regex.test(key)) { 
      obj.properties = obj.properties || {};
      obj.properties[regex.exec(key)[2]] = formData.get(key);
    } else {
      obj[key] = formData.get(key);
    }
  }

  return JSON.stringify(obj);
};

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (const key in params) {
    const hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.postLink2 = function(path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (const key in params) {
    for (const index in params[key]) {
      for (const key2 in params[key][index]) {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", `${key}[${index}][${key2}]`);
        hiddenField.setAttribute("value", params[key][index][key2]);
        form.appendChild(hiddenField);
      }
    }
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.productFormTarget = document.querySelector(this.dataset.target);
    this.input.addEventListener('change', this.onInputChange.bind(this));
    
    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    
    if(this.productFormTarget) {
      var targetFormInput = this.productFormTarget.querySelector('[name="quantity"]');

      event.target.name === 'plus' ? targetFormInput.stepUp() : targetFormInput.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus?.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus?.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('quantity-input', QuantityInput);

class DrawerCloseButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', () => this.dispatchEvent(new CustomEvent('drawer:force-close', { bubbles: true, cancelable: true, composed: true })));
  }
}
customElements.define('drawer-close-button', DrawerCloseButton);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      open: 'menu-drawer--open',
      opening: 'menu-drawer--opening',
      closing: 'menu-drawer--closing'
    };
    this.bodyStateClasses = {};
    this.animationDuration = 180;

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  setClasses(classes) {
    this.classes = classes;
  }

  setBodyStateClasses(classes) {
    this.bodyStateClasses = classes;
  }

  getAnimationDuration() {
    if (!this.mainDetailsToggle) return this.animationDuration;

    const cssDuration = Number.parseFloat(
      window.getComputedStyle(this.mainDetailsToggle).getPropertyValue('--drawer-animation-duration')
    );

    return Number.isFinite(cssDuration) ? cssDuration : this.animationDuration;
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button[data-close]').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    this.addEventListener('drawer:force-close', (event) => {
      event.stopPropagation();
      this.closeMenuDrawer(event);
    });
  }

  onKeyUp(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
  if (charCode !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 20);
    }
  }

  openMenuDrawer(summaryElement = false) {
    setScrollbarWidth();
    setHeaderBottomPosition();

    if (summaryElement) {
      summaryElement.setAttribute('aria-expanded', true);
      trapFocus(this.mainDetailsToggle, summaryElement);
    }

    setTimeout(() => {
      this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      document.body.addEventListener('click', this.onBodyClickEvent);
    });
    document.body.classList.add(this.classes.opening);
    if (this.bodyStateClasses.opening) document.body.classList.add(this.bodyStateClasses.opening);

    this.openAnimation();
    setTimeout(() => {
      this.mainDetailsToggle.setAttribute('open', '');
      setTimeout(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      }, 10);
    });
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (this._openRafId) {
      window.cancelAnimationFrame(this._openRafId);
      this._openRafId = null;
      document.body.classList.remove(this.classes.opening);
      if (this.bodyStateClasses.opening) document.body.classList.remove(this.bodyStateClasses.opening);
    }

    if (event !== undefined) {
      this.mainDetailsToggle.querySelectorAll('details').forEach(details =>  {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.classList.add('menu-closing');
      removeTrapFocus(elementToFocus);

      document.body.removeEventListener('click', this.onBodyClickEvent);
      document.body.classList.remove(this.classes.open);
      document.body.classList.add(this.classes.closing);
      if (this.bodyStateClasses.open) document.body.classList.remove(this.bodyStateClasses.open);
      if (this.bodyStateClasses.closing) document.body.classList.add(this.bodyStateClasses.closing);
      
      this.closeAnimation(this.mainDetailsToggle);
      this.dispatchEvent(new CustomEvent('close'));
      document.dispatchEvent(new CustomEvent('menudrawer:close'));
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const target = event.currentTarget;
    if (target.classList.contains('noclose')) return;
    
    const detailsElement = target.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  openAnimation() {
    let animationStart;
    const animationDuration = this.getAnimationDuration();

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < animationDuration) {
        this._openRafId = window.requestAnimationFrame(handleAnimation);
      }
      else {
        this._openRafId = null;
        document.body.classList.remove(this.classes.opening);
        document.body.classList.add(this.classes.open);
        if (this.bodyStateClasses.opening) document.body.classList.remove(this.bodyStateClasses.opening);
        if (this.bodyStateClasses.open) document.body.classList.add(this.bodyStateClasses.open);
      }
    }

    this._openRafId = window.requestAnimationFrame(handleAnimation);
  }

  closeAnimation(detailsElement) {
    let animationStart;
    const animationDuration = this.getAnimationDuration();

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < animationDuration) {
        window.requestAnimationFrame(handleAnimation);
      }
      else {
        if (detailsElement === this.mainDetailsToggle) {
          document.body.classList.remove(this.classes.closing);
          if (this.bodyStateClasses.closing) document.body.classList.remove(this.bodyStateClasses.closing);
          detailsElement.classList.remove('menu-closing');
        }
        detailsElement.removeAttribute('open');

        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }

  onBodyClick(event) {
    if (!this.contains(event.target)) {
      if (this.matches('promo-quick-view-drawer')) return;

      this.closeMenuDrawer(event);
    
      dimissPromotion();
    }
  }
}
customElements.define('menu-drawer', MenuDrawer);

class CartDrawer extends MenuDrawer {
  constructor() {
    super();
    this.animationDuration = 180;

    this.setClasses({
      open: 'mini-cart--open',
      opening: 'mini-cart--opening',
      closing: 'mini-cart--closing'
    });
  }
}
customElements.define('cart-drawer', CartDrawer);

class NotificationsDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'shop-news--open',
      opening: 'shop-news--opening',
      closing: 'shop-news--closing'
    });

    this.alertText = this.querySelector('.alert');
    this.subscribeBtn = this.querySelector('#subscribe-btn');
    this.subscribeBtn.addEventListener('click', this.subscribeToNews.bind(this));
  }

  subscribeToNews() {
    this.querySelector('.alert').classList.add('hidden');
    this.querySelector('.alert').classList.remove('alert-success', 'alert-error');
    
    this.subscribeBtn.disabled = true;
    this.subscribeBtn.innerText = 'Subscribing...';

    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${ theme.shopSettings.klaviyo.apiKey }`,
          customer = window.Shopify.customer;

    const options = {
      method: 'POST',
      headers: {
        revision: '2025-01-15',
        'content-type': 'application/vnd.api+json'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'B2B notifications drawer',
            profile: {
              data: {
                type: 'profile',
                id: 'string',
                attributes: {
                  email: customer.email,
                  first_name: customer.firstName,
                  last_name: customer.lastName
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                id: theme.shopSettings.klaviyo.listId,
                type: "list"
              }
            }
          }
        }
      })
    };

    fetch(url, options)
      .then(res => {
        this.alertText.classList.remove('hidden');

        if(res.ok) {
          this.subscribeBtn.classList.add('hidden');
          this.alertText.classList.add('alert-success');
          this.alertText.innerText = 'Thank you for subscribing!';
        } else {
          this.subscribeBtn.disabled = false;
          this.subscribeBtn.innerText = 'Subscribe';
          this.alertText.classList.add('alert-error');
          this.alertText.innerText = 'There was a problem subscribing. Please try again or contact support.';
        }
      })
      .catch(err => console.error(err));
  }
}
customElements.define('notifications-drawer', NotificationsDrawer);

class FacetDrawer extends MenuDrawer {
  constructor() {
    super();

    if (this.dataset.desktop == 'true') {
      this.setClasses({
        open: 'facet-horizontal--open',
        opening: 'facet-horizontal--opening',
        closing: 'facet-horizontal--closing'
      });
    }
    else {
      this.setClasses({
        open: 'facet-drawer--open',
        opening: 'facet-drawer--opening',
        closing: 'facet-drawer--closing'
      });
    }
  }
}
customElements.define('facet-drawer', FacetDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'menu-mobile--open',
      opening: 'menu-mobile--opening',
      closing: 'menu-mobile--closing'
    });
  }
}
customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this)
    );
    this.addEventListener('keyup', (event) => {
      const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
      if (charCode === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
      });
    }
  }

  show(opener) {
    setScrollbarWidth();
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (!modal) return;

      modal.show(this.button);

      if(this.dataset.url) {
        // Only the latest click's response may update or close the modal
        const request = this.request = (this.request || 0) + 1;

        fetch(this.dataset.url, {
          method: 'GET'
        })
        .then(response => response.text())
        .then(html => {
          if (request !== this.request) return;

          const responseHTML = new DOMParser().parseFromString(html, 'text/html');
          const contentSelector = this.dataset.contentSelector || '.modal__content';
          const content = responseHTML.querySelector(contentSelector);
          if (!content) {
            this.fallback(modal);
            return;
          }

          modal.querySelector('[role=dialog] > div').innerHTML = content.innerHTML;
        })
        .catch(error => {
          console.error(error);
          if (request === this.request) this.fallback(modal);
        });
      }
    });
  }

  // Content couldn't be loaded (e.g. the source page was deleted or unpublished): close the modal
  // and continue to data-fallback-url if set, so the customer isn't stuck
  fallback(modal) {
    // Customer already closed it: don't redirect behind their back
    if (!modal.hasAttribute('open')) return;

    modal.hide();
    if (!this.dataset.fallbackUrl) return;
    const fallbackUrl = this.dataset.fallbackUrl;
    // The fallback is checkout: let the Priority Dispatch eligibility attribute settle first.
    const settle = window.PriorityDispatchSync ? window.PriorityDispatchSync.settle() : Promise.resolve();
    settle.then(() => { window.location.href = fallbackUrl; });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!this.poster) return;

    this.poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      if (content.querySelector('video-section')) {
        const deferredElement = this.appendChild(content);
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          // force autoplay for safari
          deferredElement.play();
        }
      } else {
        this.setAttribute('loaded', true);
        const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
        if (focus) deferredElement.focus();
        if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
          // force autoplay for safari
          deferredElement.play();
        }
      }
    }
  }
}
customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  init() {
    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) {
      this.currentPage = 1;
      this.totalPages = 1;
      this.classList.add('slider--no-buttons');
      this.prevButton?.setAttribute('disabled', 'disabled');
      this.nextButton?.setAttribute('disabled', 'disabled');

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = '1';
        this.pageTotalElement.textContent = '1';
      }

      this.classList.add('is-controls-ready');
      return;
    }

    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.classList.add('is-controls-ready');
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const hasSingleOrInvalidSlideState =
      !Array.isArray(this.sliderItemsToShow) ||
      this.sliderItemsToShow.length < 2 ||
      !Number.isFinite(this.sliderItemOffset) ||
      this.sliderItemOffset <= 0;

    if (hasSingleOrInvalidSlideState) {
      this.currentPage = 1;
      this.totalPages = 1;
      this.classList.add('slider--no-buttons');
      this.prevButton?.setAttribute('disabled', 'disabled');
      this.nextButton?.setAttribute('disabled', 'disabled');

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = '1';
        this.pageTotalElement.textContent = '1';
      }

      return;
    }

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
    }

    if (this.currentPage === 1 && this.currentPage === this.totalPages) {
      this.classList.add('slider--no-buttons');
    } else {
      this.classList.remove('slider--no-buttons');
    }

    if (this.enableSliderLooping) return;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (isFirstSlide || this.isSlideVisible(this.sliderItemsToShow[0])) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (isLastSlide || this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();

    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    //this.slideScrollPosition = theme.config.rtl ? this.slideScrollPosition * -1 : this.slideScrollPosition;
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }
}
customElements.define('slider-component', SliderComponent);

class ThumbnailSlider extends SliderComponent {
  constructor() {
    super();
  }

  init() {
    super.init();
    this.updateVisibleThumbnailState();
  }

  updateVisibleThumbnailState() {
    if (!this.slider) return;

    const thumbnailItems = Array.from(this.sliderItems);
    const galleryViewer = this.closest('media-gallery')?.querySelector('[id^="GalleryViewer"]');
    const visibleMediaIds = galleryViewer
      ? new Set(
        Array.from(galleryViewer.querySelectorAll('[data-media-id]'))
          .filter((element) => window.getComputedStyle(element).display !== 'none')
          .map((element) => element.dataset.mediaId)
      )
      : null;

    const visibleThumbnailItems = thumbnailItems.filter((item) => {
      if (visibleMediaIds) {
        const target = item.dataset.target;
        return Boolean(target) && visibleMediaIds.has(target);
      }

      return item.clientWidth > 0;
    });

    if (visibleMediaIds) {
      thumbnailItems.forEach((item) => {
        const target = item.dataset.target;
        item.classList.toggle('hidden', Boolean(target) && !visibleMediaIds.has(target));
      });
    }

    this.slider.dataset.mediaCount = visibleThumbnailItems.length;
    this.classList.toggle('hidden', visibleThumbnailItems.length < 2);
    this.classList.add('is-controls-ready');
  }
}
customElements.define('thumbnail-slider', ThumbnailSlider);

class QuoteSlider extends SliderComponent {
  constructor() {
    super();
  }

  update() {
    super.update();

    if (this.currentPageElement && this.pageTotalElement) {
      this.sliderItems.forEach((element) => {
        element.removeAttribute('aria-current');
      });

      const currentElement = this.sliderItemsToShow[this.currentPage - 1];
      currentElement.setAttribute('aria-current', true);
    }
  }
}
customElements.define('quote-slider', QuoteSlider);

class ProductGallery extends SliderComponent {
  static dotCounterThreshold = 15;

 constructor() {
    super();
    this.autoplayResumeTimeout = null;
    this.autoplayDelay = Number.parseInt(this.dataset.speed, 10);
    this.handleAutoplayInteraction = this.scheduleAutoplayResume.bind(this);
    this.handleAutoplayNavClick = this.onAutoplayNavClick.bind(this);
    this.handleCarouselChange = this.onCarouselChange.bind(this);
    this.carouselDots = [];
    this.handleCarouselDotClick = this.onCarouselDotClick.bind(this);
    this.nativeAutoplayTimer = null;
    this.nativeAutoplayBound = false;
    this.nativeAutoplayHovered = false;
    this.nativeAutoplayFocused = false;
    this.nativeAutoplayStopped = false;
    this.nativeAutoplayOffscreen = false;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleNativeAutoplayInteraction = this.onNativeAutoplayInteraction.bind(this);
    this.handleAutoplayPointerEnter = this.onAutoplayPointerEnter.bind(this);
    this.handleAutoplayPointerLeave = this.onAutoplayPointerLeave.bind(this);
    this.handleAutoplayFocusIn = this.onAutoplayFocusIn.bind(this);
    this.handleAutoplayFocusOut = this.onAutoplayFocusOut.bind(this);
    this.handleReducedMotionChange = this.onReducedMotionChange.bind(this);
    
    if(this.querySelector('.carousel')) {
      // 1. Parse the existing settings from the Liquid variable
      const settings = JSON.parse(this.dataset.settings);

      // 2. Check if the 'data-speed' attribute exists (added in Step 1)
      if (Number.isFinite(this.autoplayDelay) && this.autoplayDelay > 0) {
        // Add the autoPlay option to Flickity (value is in milliseconds)
        settings.autoPlay = this.autoplayDelay;
        
        // Optional: Ensure it pauses when the user hovers (Flickity default is true, but good to be explicit)
        settings.pauseAutoPlayOnHover = true;

        // 3. FORCE the slider to loop infinitely (Required for smooth autoplay)
        settings.wrapAround = true;
      }

      // 3. Initialize Flickity with the UPDATED settings
      this.carousel = new Flickity(this.querySelector('.carousel'), settings);
      this.carousel.on('change', this.handleCarouselChange);
      this.onCarouselChange(this.carousel.selectedIndex || 0);
      this.updateCarouselControlVisibility();

      if (this.carousel && Number.isFinite(this.autoplayDelay) && this.autoplayDelay > 0) {
        this.carousel.on('uiChange', this.handleAutoplayInteraction);
        this.carousel.on('dragEnd', this.handleAutoplayInteraction);
        this.carousel.on('pointerUp', this.handleAutoplayInteraction);
        this.carousel.element.addEventListener('click', this.handleAutoplayNavClick);
      }
    }
  }

  onAutoplayNavClick(event) {
    if (!event.target.closest('.flickity-prev-next-button, .flickity-page-dot')) return;
    this.scheduleAutoplayResume();
  }

  scheduleAutoplayResume() {
    if (!this.carousel || !Number.isFinite(this.autoplayDelay) || this.autoplayDelay <= 0) return;

    this.clearAutoplayResumeTimeout();
    this.autoplayResumeTimeout = window.setTimeout(() => {
      if (!this.isConnected || !this.carousel || typeof this.carousel.playPlayer !== 'function') return;
      this.carousel.options.autoPlay = this.autoplayDelay;
      if (this.carousel.player) this.carousel.player.autoPlay = this.autoplayDelay;
      this.carousel.playPlayer();
    }, this.autoplayDelay);
  }

  clearAutoplayResumeTimeout() {
    if (!this.autoplayResumeTimeout) return;
    window.clearTimeout(this.autoplayResumeTimeout);
    this.autoplayResumeTimeout = null;
  }

  invalidateCarouselVisibilityCache() {
    this.visibleCarouselMediaCount = null;
  }

  updateCarouselControlVisibility({ forceRecompute = false, syncThumbnails = false } = {}) {
    if (!this.carousel || !this.carousel.element) return;

    // IMPORTANT: any code path that changes media visibility (display/hidden classes)
    // must either call invalidateCarouselVisibilityCache() or pass forceRecompute: true.
    if (forceRecompute || this.visibleCarouselMediaCount == null) {
      const visibleCarouselItems = Array.from(this.querySelectorAll('.carousel [data-media-id]')).filter((element) => {
        return window.getComputedStyle(element).display !== 'none';
      });
      this.visibleCarouselMediaCount = visibleCarouselItems.length;
    }
    const shouldHideControls = this.visibleCarouselMediaCount < 2;

    this.carousel.element.querySelector('.flickity-page-dots')?.classList.toggle('hidden', shouldHideControls);
    this.carousel.element.querySelectorAll('.flickity-prev-next-button').forEach((button) => {
      button.classList.toggle('hidden', shouldHideControls);
    });

    this.querySelector(':scope > .slider-buttons')?.classList.toggle('hidden', shouldHideControls);
    this.classList.toggle('slider--no-buttons', shouldHideControls);
    if (syncThumbnails) {
      this.closest('media-gallery')?.querySelector('[id^="GalleryThumbnails"]')?.updateVisibleThumbnailState?.();
    }
    this.classList.add('is-controls-ready');
  }

  syncCarousel(selectedMediaId = null, isInstant = false) {
    if (!this.carousel) return;

    if (typeof this.carousel.reloadCells === 'function') {
      this.carousel.reloadCells();
    }

    if (typeof this.carousel.resize === 'function') {
      this.carousel.resize();
    }

    // Variant/media sync can change which slides are visible.
    this.invalidateCarouselVisibilityCache();
    this.updateCarouselControlVisibility({
      forceRecompute: true,
      syncThumbnails: true
    });

    const selectedElement = selectedMediaId
      ? this.querySelector(`[data-media-id="${selectedMediaId}"]`)
      : this.querySelector('.is-active[data-media-id]') || this.carousel.selectedElement;

    if (selectedElement && typeof this.carousel.selectCell === 'function') {
      this.carousel.selectCell(selectedElement, false, isInstant);
      return;
    }

    this.onCarouselChange(this.carousel.selectedIndex || 0);
  }

  onCarouselChange(index) {
    if (!this.carousel) return;

    const currentElement = this.carousel.cells?.[index]?.element || this.carousel.selectedElement;
    if (!currentElement) return;

    this.updateCarouselControlVisibility();

    this.querySelectorAll('[data-media-id]').forEach((element) => {
      element.classList.toggle('is-active', element === currentElement);
    });

    this.dispatchEvent(new CustomEvent('slideChanged', {
      detail: {
        currentPage: index + 1,
        currentElement
      }
    }));
  }

  init() {
    super.init();

    if(this.carousel) {
      this.carousel.resize();
      // Initial render state may hide non-active media.
      this.invalidateCarouselVisibilityCache();
      this.updateCarouselControlVisibility({
        forceRecompute: true,
        syncThumbnails: true
      });
    }

    this.cursor = this.querySelector('.gallery-cursor');
    if (!this.cursor) return;

    const images = this.querySelectorAll('.product__modal-opener--image');
    images.forEach((image) => image.addEventListener('mousemove', this.onMoveHandler.bind(this)));
    images.forEach((image) => image.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    images.forEach((image) => image.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));
  }

  onMoveHandler(event) {
    this.cursor.style.left = `${event.clientX - 32}px`;
    this.cursor.style.top = `${event.clientY - 32}px`;
  }

  onEnterHandler() {
    this.cursor.classList.add('show');
  }

  onLeaveHandler() {
    this.cursor.classList.remove('show');
  }

  get nativeAutoplayEnabled() {
    if (this.carousel) return false;
    if (!Number.isFinite(this.autoplayDelay) || this.autoplayDelay <= 0) return false;
    if (this.reducedMotionQuery.matches) return false;
    return Boolean(this.querySelector('.slider-buttons--gallery'));
  }

  setupNativeAutoplay() {
    if (!this.isConnected) return;
    if (this.nativeAutoplayBound) return;
    if (this.carousel) return;
    if (!Number.isFinite(this.autoplayDelay) || this.autoplayDelay <= 0) return;
    if (!this.querySelector('.slider-buttons--gallery')) return;
    this.nativeAutoplayBound = true;

    this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange);
    this.addEventListener('pointerenter', this.handleAutoplayPointerEnter);
    this.addEventListener('pointerleave', this.handleAutoplayPointerLeave);
    this.addEventListener('focusin', this.handleAutoplayFocusIn);
    this.addEventListener('focusout', this.handleAutoplayFocusOut);
    this.slider.addEventListener('pointerdown', this.handleNativeAutoplayInteraction);
    this.querySelector('.slider-buttons--gallery')
      .addEventListener('click', this.handleNativeAutoplayInteraction);

    this.nativeAutoplayObserver = new IntersectionObserver((entries) => {
      this.nativeAutoplayOffscreen = !entries.some((entry) => entry.isIntersecting);
    });
    this.nativeAutoplayObserver.observe(this);
  }

  startNativeAutoplay() {
    if (!this.isConnected) return;
    if (this.nativeAutoplayStopped) return;
    if (!this.nativeAutoplayEnabled) return;
    if (this.nativeAutoplayTimer) return;
    if (!Array.isArray(this.sliderItemsToShow) || this.sliderItemsToShow.length < 2) return;

    this.nativeAutoplayTimer = window.setInterval(() => {
      if (!this.isConnected) return this.stopNativeAutoplay();
      if (document.hidden) return;
      if (this.nativeAutoplayHovered) return;
      if (this.nativeAutoplayFocused) return;
      if (this.nativeAutoplayOffscreen) return;
      this.advanceNativeAutoplay();
    }, this.autoplayDelay);
  }

  stopNativeAutoplay() {
    if (!this.nativeAutoplayTimer) return;
    window.clearInterval(this.nativeAutoplayTimer);
    this.nativeAutoplayTimer = null;
  }

  advanceNativeAutoplay() {
    const slides = this.sliderItemsToShow;
    if (!Array.isArray(slides) || slides.length < 2) return;

    const nextIndex = this.currentPage >= slides.length ? 0 : this.currentPage;
    const target = slides[nextIndex];
    if (!target) return;

    this.slider.scrollTo({ left: target.offsetLeft - slides[0].offsetLeft });
  }

  onNativeAutoplayInteraction() {
    this.nativeAutoplayStopped = true;
    this.clearAutoplayResumeTimeout();
    this.stopNativeAutoplay();
  }

  onAutoplayPointerEnter() {
    this.nativeAutoplayHovered = true;
  }

  onAutoplayPointerLeave() {
    this.nativeAutoplayHovered = false;
  }

  onAutoplayFocusIn() {
    this.nativeAutoplayFocused = true;
  }

  onAutoplayFocusOut() {
    this.nativeAutoplayFocused = false;
  }

  onReducedMotionChange() {
    if (this.nativeAutoplayEnabled) {
      this.startNativeAutoplay();
    } else {
      this.stopNativeAutoplay();
    }
  }

  onCarouselDotClick(event) {
    if (event.currentTarget.disabled) return;

    const index = this.carouselDots.indexOf(event.currentTarget);
    const slide = this.sliderItemsToShow?.[index];
    if (!slide) return;

    this.slider.scrollTo({
      left: slide.offsetLeft - this.sliderItemsToShow[0].offsetLeft
    });
  }

  clearNativeDots(container) {
    this.carouselDots.forEach((dot) => dot.removeEventListener('click', this.handleCarouselDotClick));
    this.carouselDots = [];
    container?.replaceChildren();
  }

  syncNativeDots() {
    const container = this.querySelector('[data-gallery-dots]');
    if (!container) return;

    const slides = Array.isArray(this.sliderItemsToShow) ? this.sliderItemsToShow : [];
    const counter = this.querySelector('[data-gallery-counter]');
    const useCounter = slides.length > ProductGallery.dotCounterThreshold;

    container.hidden = useCounter || slides.length < 2;
    if (counter) counter.hidden = !useCounter;

    if (container.hidden) {
      this.clearNativeDots(container);
      return;
    }

    if (this.carouselDots.length !== slides.length) {
      this.clearNativeDots(container);
      this.carouselDots = slides.map((slide) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'gallery-dots__dot';
        dot.dataset.mediaId = slide.dataset.mediaId || '';
        dot.setAttribute('data-gallery-dot', '');
        dot.addEventListener('click', this.handleCarouselDotClick);
        return dot;
      });
      container.replaceChildren(...this.carouselDots);
    }

    this.carouselDots.forEach((dot, index) => {
      if (index === this.currentPage - 1) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
      dot.setAttribute('aria-label', `${index + 1} ${this.dataset.labelOf || 'of'} ${slides.length}`);
    });
  }

  initPages() {
    super.initPages();
    this.syncNativeDots();
    this.setupNativeAutoplay();
    this.startNativeAutoplay();
  }

  update() {
    super.update();

    if (this.currentPageElement && this.pageTotalElement) {
      const currentElement = this.sliderItemsToShow[this.currentPage - 1];
      if (currentElement && currentElement.querySelector('.media')) {
        this.slider.style.setProperty('--force-image-ratio-percent', currentElement.querySelector('.media').style.getPropertyValue('--image-ratio-percent'));
      }
    }

    this.syncNativeDots();
  }

  goToFirstSlide() {
    this.slider.scrollTo({
      left: 0
    });
  }

  disconnectedCallback() {
    this.clearAutoplayResumeTimeout();
    this.clearNativeDots(this.querySelector('[data-gallery-dots]'));
    this.stopNativeAutoplay();
    this.nativeAutoplayObserver?.disconnect();
    this.nativeAutoplayObserver = null;
    this.nativeAutoplayBound = false;
    this.nativeAutoplayHovered = false;
    this.nativeAutoplayFocused = false;
    this.nativeAutoplayOffscreen = false;
    this.reducedMotionQuery.removeEventListener('change', this.handleReducedMotionChange);
    this.removeEventListener('pointerenter', this.handleAutoplayPointerEnter);
    this.removeEventListener('pointerleave', this.handleAutoplayPointerLeave);
    this.removeEventListener('focusin', this.handleAutoplayFocusIn);
    this.removeEventListener('focusout', this.handleAutoplayFocusOut);
    this.slider?.removeEventListener('pointerdown', this.handleNativeAutoplayInteraction);
    this.querySelector('.slider-buttons--gallery')
      ?.removeEventListener('click', this.handleNativeAutoplayInteraction);

    if (this.carousel && this.handleCarouselChange && typeof this.carousel.off === 'function') {
      this.carousel.off('change', this.handleCarouselChange);
    }

    if (this.carousel && this.handleAutoplayInteraction && typeof this.carousel.off === 'function') {
      this.carousel.off('uiChange', this.handleAutoplayInteraction);
      this.carousel.off('dragEnd', this.handleAutoplayInteraction);
      this.carousel.off('pointerUp', this.handleAutoplayInteraction);
    }

    if (this.carousel && this.handleAutoplayNavClick && this.carousel.element) {
      this.carousel.element.removeEventListener('click', this.handleAutoplayNavClick);
    }
  }
}
customElements.define('product-gallery', ProductGallery);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    
    // Add class "last-visible" to the last visible option if quantity selector is present
    if(this.classList.contains('has-quantity-selector')) {
      var options = this.querySelectorAll('.product-form__input--dropdown:not(.hidden)'),
          lastVisibleOption = options[options.length - 1];
      
      if (lastVisibleOption) {
        lastVisibleOption.classList.add('last-visible');
      }
    }
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      if(event.target.name == 'quantity') {
        return;
      }
      
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  toggleAddButton(disabled, text, loading, buttonText) {
    const addButton = this.closest('form').querySelector('.product-form__submit');
    if (!addButton) return;

    addButton.disabled = disabled;
    addButton.textContent = disabled ? text : buttonText;
    addButton.classList.toggle('loading', loading);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected') != null)
        .removeAttribute('selected');

      target.selectedOptions[0].setAttribute('selected', 'selected');

      if(this.dataset.section.indexOf('promotion') >= 0) {
        const variants = JSON.parse(this.closest('form').querySelector('script').innerText),
              currentVariant = variants.find(variant => {
                if(variant.option1 == target.selectedOptions[0].value) return true;
                if(variant.option2 == target.selectedOptions[0].value) return true;
                if(variant.option3 == target.selectedOptions[0].value) return true;
            });

        this.toggleAddButton(true, window.languages.products.product.unavailable, false, window.languages.promotions.claim_gift);
  
        if(currentVariant.available) {
          this.closest('form').querySelector('[name=id]').value = currentVariant.id;
          this.toggleAddButton(false, window.languages.products.product.unavailable, false, window.languages.promotions.claim_gift);
        }
  
        return;
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked, .product-form__input--custom-dropdown input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);


class ProgressBar extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    setTimeout(() => {
      const quantity = parseInt(this.dataset.quantity);
      this.style.setProperty('--progress-bar-width', `${quantity * 100 / 30}%`);
    }, 1e3);
  }
}
customElements.define('progress-bar', ProgressBar);

class FormState extends HTMLElement {
  constructor() {
    super();

    this.formInputs = this.querySelectorAll('input,select,textarea');
    this.form = this.querySelector('form');

    this.formInputs.forEach((input) => {
      input.addEventListener('input', this.onInputChange.bind(this));
      input.addEventListener('blur', this.onInputChange.bind(this));
    });

    if (this.form) this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  onInputChange(event) {
    this.handleInputCheck(event.target);
  }

  onSubmitHandler(event) {
    let valid = !0;

    this.formInputs.forEach((input) => {
      if (!this.handleInputCheck(input)) {
        valid = !1;
      }
    });

    if (!valid) {
      event.preventDefault();
      return;
    }
  }

  handleInputCheck(input) {
    if (input.classList.contains('required')) {

      if (input.value.length === 0 || input.value === input.dataset.empty) {
        input.classList.remove('valid');
        input.classList.add('invalid');
        
        return !1;
      }
      else {
        input.classList.remove('invalid');
        input.classList.add('valid');

        return !0;
      }
    }

    return !0;
  }
}
customElements.define('form-state', FormState);

class ListMenuItem extends HTMLElement {
  constructor() {
    super();

    this.listMenuHover = this.closest('menu-drawer').querySelector('list-menu-hover');

    this.addEventListener('mouseenter', () => {
      this.listMenuHover.open({
        title: this.dataset.title,
        url: this.dataset.url,
        image: this.dataset.image,
        description: this.dataset.description
      });
    });
  }
}
customElements.define('list-menu-item', ListMenuItem);

class ListMenuHover extends HTMLElement {
  constructor() {
    super();
  }

  open(obj) {
    let html = '';
    const opened = obj.image.length > 0 || obj.description.length > 0;

    if (opened) {
      html += `<a href="${obj.url}" class="hover-collection${obj.image.length > 0 ? '' : ' hover-collection--no-image'}">`;
        html += '<figure class="hover-collection__image"' + (obj.image.length > 0 ? `style="background-image:url(${obj.image})"` : '') + '></figure>';
        html += '<div class="hover-collection__content">'
          html += `<span class="hover-collection__title h4">${obj.title}</span>`;
          html += obj.description.length > 0 ? `<div class="hover-collection__description">${obj.description}</div>` : '';
        html += '</div>';
      html += '</a>';
    }

    this.innerHTML = html;
  }

  close() {
    this.innerHTML = '';
  }
}
customElements.define('list-menu-hover', ListMenuHover);

class TransparentHeader extends HTMLElement {
  constructor() {
    super();

    const header = document.querySelector('.shopify-section-header');

    header.classList.add('transparent-header');
    if (this.dataset.showSeparator == 'true') {
      header.classList.add('transparent-separator');
    }
  }
}
customElements.define('transparent-header', TransparentHeader);

class AddToCart extends HTMLElement {
  constructor() {
    super();

    this.miniCart = document.querySelector('mini-cart');
    this.addEventListener('click', this.onClickHandler.bind(this));
    this.addEventListener('keydown', this.onKeydownHandler.bind(this));
  }

  connectedCallback() {
    // No native button semantics, so the drawer focus selectors skip this
    // element and screen readers do not announce it without these.
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
  }

  onKeydownHandler(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

    // Space would scroll the page on a non-button element.
    event.preventDefault();

    // keydown repeats while the key is held. The cart page path returns before
    // disabled is set, so without this a held key adds the item repeatedly.
    if (event.repeat) return;

    this.onClickHandler();
  }

  onClickHandler() {
    // disabled is inert on a custom element and no CSS blocks pointer events,
    // so without this guard an in-flight request can be submitted twice.
    if (this.hasAttribute('disabled')) return;
    const variantId = this.dataset.variantId;
    const atcSource = this.dataset.atcSource || 'main-atc';

    if (variantId) {
      if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
        Shopify.postLink(theme.routes.cart_add_url, {
          parameters: {
            id: variantId,
            quantity: 1,
            'properties[__atc_source]': atcSource
          },
        });
        return;
      }

      this.setAttribute('disabled', true);
      this.setAttribute('aria-disabled', 'true');

      // Optimistic confirmation for mini cart upsells: the shared .loading state
      // hides the label for the whole round trip, so confirm on click and roll back
      // if the request fails.
      const optimistic = atcSource === 'mini-cart-recommendation';
      const optimisticLabel = optimistic ? this.querySelector('.label') : null;
      let previousLabel = null;

      if (optimistic) {
        if (optimisticLabel) {
          previousLabel = optimisticLabel.textContent;
          optimisticLabel.textContent = this.dataset.addedLabel || 'Added';
        }
        this.classList.add('atc-added');
      } else {
        this.classList.add('loading');
      }

      const rollbackOptimistic = () => {
        if (!optimistic) return;
        this.classList.remove('atc-added');
        if (optimisticLabel && previousLabel !== null) {
          optimisticLabel.textContent = previousLabel;
        }
      };

      const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.section || section.id) : [];

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { '__atc_source': atcSource },
        sections: sections,
        sections_url: window.location.pathname
      });

      fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
        .then(async (response) => {
          const parsedState = await response.json();

          if (!response.ok) {
            rollbackOptimistic();
            document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
              detail: {
                errorMessage: parsedState.description || parsedState.message || 'Unable to add this item to the cart.'
              }
            }));
            return null;
          }

          return parsedState;
        })
        .then((parsedState) => {
          if (!parsedState) return;

          if (parsedState.status === 422) {
             rollbackOptimistic();
             document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
                detail: {
                  errorMessage: parsedState.description
                }
              }));
           }
           else {
            this.miniCart && this.miniCart.renderContents(parsedState);

             document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: {
                product: parsedState,
                productDetails: this.querySelector('script.product-details') ? this.querySelector('script.product-details').innerHTML : {}
              }
            }));
          }
        })
        .catch((e) => {
          rollbackOptimistic();
          console.error(e);
        })
        .finally(() => {
          this.classList.remove('loading');
          this.removeAttribute('disabled');
          this.removeAttribute('aria-disabled');
        });
    }
  }
}
customElements.define('add-to-cart', AddToCart);

class PriceMoney extends HTMLElement {
  constructor() {
    super();

    if (this.shouldInit()) {
      this.init();
    }
  }

  shouldInit() {
    if (document.body.dataset.priceSuperscript === undefined) {
      return false;
    }  

    const moneyFormat = theme.shopSettings.moneyFormat.toLowerCase();
    if (moneyFormat.indexOf('class=') !== -1) {
      return false;
    }
    
    return true;
  }

  init() {
    const currencies_using_comma_decimals = 'ANG,ARS,BRL,BYN,BYR,CLF,CLP,COP,CRC,CZK,DKK,EUR,HRK,HUF,IDR,ISK,MZN,NOK,PLN,RON,RUB,SEK,TRY,UYU,VES,VND'.split(',');
    const symbol = theme.shopSettings.moneyFormat.replace(/\{{.*}}/, '').trim();
    const bdi = this.querySelector('bdi');
    const price = bdi.textContent.replace(theme.shopSettings.isoCode,'');
    let html = price.replace(' ', '');
    let money_symbol_decimal = '.';
    if (currencies_using_comma_decimals.includes(theme.shopSettings.isoCode)) {
      money_symbol_decimal = ',';
    }
    if (price.includes(money_symbol_decimal)) {
      if (price.lastIndexOf(symbol) + symbol.length === price.length) {
        const price_without_symbol = price.slice(0, price.lastIndexOf(symbol));
        const price_without_decimal = price.slice(0, price.lastIndexOf(money_symbol_decimal));
        const decimal = price_without_symbol.replace(price_without_decimal, '').trim();
        html = html.replace(decimal, `<sup class="price__suffix" data-suffix="${ decimal }">${decimal}</sup>`);
      }
      else {
        const price_without_decimal = price.slice(0, price.lastIndexOf(money_symbol_decimal));
        const decimal = price.replace(price_without_decimal, '').trim();
        html = html.replace(decimal, `<sup class="price__suffix" data-suffix="${ decimal }">${decimal}</sup>`);
      }
    }
    html = html.replace(symbol, `<span class="price__prefix">${symbol}</span>`);
    if(theme.shopSettings.currencyCode){
       html = html + " " + theme.shopSettings.isoCode;
    }
    bdi.innerHTML = html;
  }
}
customElements.define('price-money', PriceMoney);

class SlideshowComponent extends HTMLElement {
  constructor() {
    super();

    this.elements = {
      small: this.querySelector('.slideshow__left'),
      content: this.querySelector('.slideshow__content'),
      large: this.querySelector('.slideshow__right')
    }
    this.settings = {
      autorotate: this.dataset.autorotate == 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed),
      slidesSmall: this.elements.small.querySelectorAll('.slideshow__image'),
      slidesLarge: this.elements.large.querySelectorAll('.slideshow__image')
    };

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
    this.matchMedia();
  }

  init() {
    setTimeout(() => {
      this.flickitySmall = new Flickity(this.elements.small, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        draggable: false,
        setGallerySize: false,
        autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
      });

      this.flickityContent = new Flickity(this.elements.content, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: true,
        pageDots: true,
        wrapAround: true,
        draggable: theme.config.mqlSmall ? true : false,
        asNavFor: this.elements.small,
        adaptiveHeight: true,
        arrowShape: 'M29.043 19.756l-28.125 28.125c-0.605 0.606-0.918 1.406-0.918 2.031s0.305 1.599 0.916 2.209l28.125 28.125c1.221 1.221 3.199 1.221 4.418 0s1.221-3.199 0-4.418l-22.793-22.793h86.209c1.727 0 3.125-1.398 3.125-2.949 0-1.727-1.398-3.125-3.125-3.125h-86.211l22.793-22.793c1.221-1.221 1.221-3.199 0-4.418s-3.203-1.217-4.414 0.006z',
        on: {
          ready: () => {
            const prevNextButtons = this.querySelectorAll('.flickity-button');
            if (prevNextButtons) {
              prevNextButtons.forEach((button) => {
                button.setAttribute('tabindex', '-1');
              });
            }
          }
        }
      });

      this.flickityLarge = new Flickity(this.elements.large, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        draggable: true,
        setGallerySize: false,
        asNavFor: this.elements.content,
        dragThreshold: 10
      });

      this.bindEvents();
    });
  }

  bindEvents() {
    this.flickitySmall.on('scroll', () => {
      this.flickitySmall.slides.forEach((slide, i) => {
        const flickity = this.flickitySmall;
        const image = this.settings.slidesSmall[i];
        let x = 0;

        if (image) {
          if( 0 === i ) {
            x = Math.abs( flickity.x ) > flickity.slidesWidth ? ( flickity.slidesWidth + flickity.x + flickity.slides[flickity.slides.length - 1 ].outerWidth + slide.target ) : ( slide.target + flickity.x );
          }
          else if( i === flickity.slides.length - 1 ) {
            x = Math.abs( flickity.x ) + flickity.slides[i].outerWidth < flickity.slidesWidth ? ( slide.target - flickity.slidesWidth + flickity.x - flickity.slides[i].outerWidth ) : ( slide.target + flickity.x );
          }
          else {
            x = slide.target + flickity.x;
          }
          
          if (!theme.config.isTouch && !theme.config.rtl) {
            image.style.transform = 'translateX(' + x * ( -1 / 2 ) + 'px)';
          }
        }
      });
    });

    this.flickityLarge.on('scroll', () => {
      this.flickityLarge.slides.forEach((slide, i) => {
        const flickity = this.flickityLarge;
        const image = this.settings.slidesLarge[i];
        let x = 0;

        if (image) {
          if( 0 === i ) {
            x = Math.abs( flickity.x ) > flickity.slidesWidth ? ( flickity.slidesWidth + flickity.x + flickity.slides[flickity.slides.length - 1 ].outerWidth + slide.target ) : ( slide.target + flickity.x );
          }
          else if( i === flickity.slides.length - 1 ) {
            x = Math.abs( flickity.x ) + flickity.slides[i].outerWidth < flickity.slidesWidth ? ( slide.target - flickity.slidesWidth + flickity.x - flickity.slides[i].outerWidth ) : ( slide.target + flickity.x );
          }
          else {
            x = slide.target + flickity.x;
          }

          if (!theme.config.isTouch && !theme.config.rtl) {
            image.style.transform = 'translateX(' + x * ( -1 / 2 ) + 'px)';
          }
        }
      });
    });

    this.elements.content.querySelector('.flickity-button.next').addEventListener('click', () => {
      this.flickitySmall && this.flickitySmall.next();
      this.stopPlayer();
    });

    this.elements.content.querySelector('.flickity-button.previous').addEventListener('click', () => {
      this.flickitySmall && this.flickitySmall.previous();
      this.stopPlayer();
    });

    this.elements.content.querySelectorAll('.flickity-page-dot').forEach((button) => {
      button.addEventListener('click', () => {
        this.stopPlayer();
      });
    });

    let currentIndex = null,
      swipeDirection = null;
  
    // Detect swipe direction
    this.flickityLarge.on('dragMove',  (_event, _pointer, moveVector) => {
      currentIndex = this.flickityLarge.selectedIndex;
      swipeDirection = this.getSwipeDirection(moveVector);

      this.stopPlayer();
    });
    
    // Do stuff based on a successful swipe and it's direction
    this.flickityLarge.on('dragEnd', () => {
      if (this.flickityLarge.selectedIndex !== currentIndex) {
        
        if (swipeDirection === 'left') {
          this.flickitySmall.next();
        }
        else {
          this.flickitySmall.previous();
        }
      }
    });
  }

  stopPlayer() {
    if (this.settings.autorotate) {
      this.flickitySmall && this.flickitySmall.stopPlayer();
    }
  }

  getSwipeDirection(moveVector) {
    return moveVector.x > 0 ? 'right' : 'left';
  }

  matchMedia() {
    document.addEventListener('matchSmall', () => {
      this.unload();
      this.init();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.init();
    });
  }

  unload() {
    if (this.flickitySmall && typeof this.flickitySmall.destroy === 'function') {
      this.flickitySmall.destroy();
    }
    if (this.flickityContent && typeof this.flickityContent.destroy === 'function') {
      this.flickityContent.destroy();
    }
    if (this.flickityLarge && typeof this.flickityLarge.destroy === 'function') {
      this.flickityLarge.destroy();
    }
  }
}
customElements.define('slideshow-component', SlideshowComponent);

class TestimonialsComponent extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.slider == 'true') {
      this.settings = {
        autorotate: this.dataset.autorotate == 'true',
        autorotateSpeed: parseInt(this.dataset.autorotateSpeed)
      };
      this.slider = this.querySelector('.testimonial__list');
      this.previews = this.querySelector('.testimonial__previews');
      this.flickityOptions = {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: true,
        arrowShape: 'M29.043 19.756l-28.125 28.125c-0.605 0.606-0.918 1.406-0.918 2.031s0.305 1.599 0.916 2.209l28.125 28.125c1.221 1.221 3.199 1.221 4.418 0s1.221-3.199 0-4.418l-22.793-22.793h86.209c1.727 0 3.125-1.398 3.125-2.949 0-1.727-1.398-3.125-3.125-3.125h-86.211l22.793-22.793c1.221-1.221 1.221-3.199 0-4.418s-3.203-1.217-4.414 0.006z',
        pageDots: this.dataset.pageDots || false,
        wrapAround: this.dataset.wrapAround || true,
        draggable: this.dataset.draggable || true,
        cellAlign: this.dataset.cellAlign || 'center',
        autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
        pauseAutoPlayOnHover: true,
        asNavFor: this.previews
      };

      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600
      });
      theme.initWhenVisible({
        element: this,
        callback: this.update.bind(this),
        threshold: 0
      });
    }

    if(this.dataset.slideshowType == 'modern') {
      document.addEventListener('matchSmall', this.matchSmall.bind(this));
      document.addEventListener('unmatchSmall', this.unmatchSmall.bind(this));
    }

    window.addEventListener('resize', this.resizeHandler.bind(this));
  }

  init() {
    setTimeout(() => {
      if (this.slider && ( ! theme.config.mqlSmall || (theme.config.mqlSmall && this.dataset.slideshowType != 'modern'))) {
        this.slider.classList.add('is-loaded');
        this.flickity = new Flickity(this.slider, this.flickityOptions);
        this.sliderFlickity = true;
      }

      this.previews.classList.add('is-loaded');
      this.flickityNav = new Flickity(this.previews, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: this.flickity ? false : true,
        arrowShape: this.flickity ? '' : 'M29.043 19.756l-28.125 28.125c-0.605 0.606-0.918 1.406-0.918 2.031s0.305 1.599 0.916 2.209l28.125 28.125c1.221 1.221 3.199 1.221 4.418 0s1.221-3.199 0-4.418l-22.793-22.793h86.209c1.727 0 3.125-1.398 3.125-2.949 0-1.727-1.398-3.125-3.125-3.125h-86.211l22.793-22.793c1.221-1.221 1.221-3.199 0-4.418s-3.203-1.217-4.414 0.006z',
        pageDots: theme.config.mqlSmall && this.dataset.slideshowType == 'modern' ? true : false,
        wrapAround: true,
        selectedAttraction: 0.2,
        friction: 0.8,
        adaptiveHeight: true,
      });

      this.flickityNav.previous();
      if (this.settings.autorotate) {
        this.flickity.pausePlayer();
      }

      this.flickity && this.flickity.on('staticClick', (_event, _pointer, _cellElement, cellIndex) => {
        if (typeof cellIndex == 'number') {
          this.flickityNav.select(cellIndex);
        }
      });
      
      // Do stuff based on a successful swipe and it's direction
      this.flickity && this.flickity.on('change', (index) => {
        this.flickityNav.select(index);
      });
    });
  }

  update() {
    setTimeout(() => {
      if (this.flickity && this.flickityNav) {
        this.flickityNav.next();
        if (this.settings.autorotate) {
          this.flickity.unpausePlayer();
        }
      }
    }, 300);
  }

  getSwipeDirection(moveVector) {
    return moveVector.x > 0 ? 'right' : 'left';
  }

  matchSmall() {
    if(this.flickity) {
      this.flickity.destroy();
    }

    this.slider.parentElement.style.display = 'none';
    this.sliderFlickity = false;

    if(this.flickityNav) {
      this.flickityNav.resize();
    }
  }

  unmatchSmall() {
    this.slider.parentElement.style.display = 'block';
    
    if( ! this.sliderFlickity) {
      this.init();

      setTimeout(() => {
        if(this.flickity && typeof this.flickity.resize === 'function') {
          this.flickity.resize();
        }
      }, 500)
    }
  }

  resizeHandler() {
    if(this.flickity && typeof this.flickity.resize === 'function') {
      this.flickity.resize();
    }

    if(this.flickityNav && typeof this.flickityNav.resize === 'function') {
      this.flickityNav.resize();
    }
  }
}
customElements.define('testimonials-component', TestimonialsComponent);

class UseAnimate extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    this.setAttribute('animate', '');
  }
}
customElements.define('use-animate', UseAnimate);

class AnimateSticky extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, false);
    this.onScrollHandler();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.getOffsetTop(this)) {
      window.requestAnimationFrame(this.reveal.bind(this));
    } else {
      window.requestAnimationFrame(this.reset.bind(this));
    }
  }

  reveal() {
    this.setAttribute('animate', '');
    document.querySelector('body').classList.add('sticky-cart--open');
  }

  reset() {
    this.removeAttribute('animate');
    document.querySelector('body').classList.remove('sticky-cart--open');
  }

  getOffsetTop(element) {
    let offsetTop = 0;
    while(element) {
      offsetTop += element.offsetTop;
      element = element.offsetParent;
    }
    return offsetTop;
  }
}
customElements.define('animate-sticky', AnimateSticky);

class LookbookComponent extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    const hotspots = this.querySelectorAll('.look__hotspot');
    hotspots.forEach((hotspot) => {
      hotspot.classList.add('is-active');
    });

    setTimeout(() => {
      hotspots.forEach((hotspot) => {
        hotspot.classList.remove('is-active');
      });
    }, 3e3);
  }
}
customElements.define('lookbook-component', LookbookComponent);

class TabCollage extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('.tab-collage__heading').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );

    if (this.dataset.accordion === undefined) {
      this.querySelectorAll('.tab-collage__heading').forEach(
        (button) => button.addEventListener('mouseover', this.onButtonClick.bind(this))
      );
    }
    
    this.reloadSection();

    window.addEventListener('resize', this.reloadSection.bind(this));
  }

  onButtonClick(event) {
    const target = event.target;
    if (target.classList.contains('is-active')) {
      if (this.dataset.accordion !== undefined) {
        target.classList.remove('is-active');
      }
      return;
    }

    this.pauseAllMedia();

    this.querySelectorAll('[data-block-id]').forEach((button) => {
      if (target.dataset.blockId === button.dataset.blockId) {
        button.classList.add('is-active');
      }
      else {
        button.classList.remove('is-active');
      }
    });

    this.reloadSection();
  }

  reloadCarousel() {
    this.removeCarouselA11yListeners();

    // Reset a11y modifications on the DOM directly, even if Flickity is gone
    this.querySelectorAll('[data-a11y-orig-tabindex], [data-a11y-orig-href], [data-a11y-disabled-by-script]').forEach((el) => {
      this.restoreFocusableElement(el);
    });

    if(this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy();
    }

    if(this.dataset.carousel !== undefined) {
      setTimeout(() => {
        var carouselElement = this.querySelector('.tab-collage__item.is-active > .carousel');

        if(carouselElement) {
          this.flickity = new Flickity(carouselElement, JSON.parse(this.dataset.flickitySettings));
          this.bindCarouselA11yListeners();
          this.syncCarouselCellAccessibility();
        }
      }, 120);
    }
  }

  // --- Accessibility: prevent focusable elements inside aria-hidden Flickity cells ---

  get focusableSelector() {
    return 'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  }

  bindCarouselA11yListeners() {
    if (!this.flickity || typeof this.flickity.on !== 'function') return;

    this._onCarouselA11yUpdate = this.syncCarouselCellAccessibility.bind(this);
    this.flickity.on('change', this._onCarouselA11yUpdate);
    this.flickity.on('settle', this._onCarouselA11yUpdate);
  }

  removeCarouselA11yListeners() {
    if (!this.flickity || typeof this.flickity.off !== 'function' || !this._onCarouselA11yUpdate) return;

    this.flickity.off('change', this._onCarouselA11yUpdate);
    this.flickity.off('settle', this._onCarouselA11yUpdate);
    this._onCarouselA11yUpdate = null;
  }

  syncCarouselCellAccessibility() {
    if (!this.flickity || !this.flickity.element) return;

    this.flickity.element.querySelectorAll('.flickity-slider > *').forEach((cell) => {
      const isHidden = cell.getAttribute('aria-hidden') === 'true';
      if (isHidden) {
        cell.querySelectorAll(this.focusableSelector).forEach((el) => {
          this.hideFocusableElement(el);
        });
      } else {
        cell.querySelectorAll('[data-a11y-orig-tabindex], [data-a11y-orig-href], [data-a11y-disabled-by-script]').forEach((el) => {
          this.restoreFocusableElement(el);
        });
      }
    });
  }


  hideFocusableElement(element) {
    if (!element.hasAttribute('data-a11y-orig-tabindex')) {
      element.setAttribute('data-a11y-orig-tabindex', element.getAttribute('tabindex') || '');
    }
    element.setAttribute('tabindex', '-1');

    if ((element.tagName === 'A' || element.tagName === 'AREA') && element.hasAttribute('href') && !element.hasAttribute('data-a11y-orig-href')) {
      element.setAttribute('data-a11y-orig-href', element.getAttribute('href'));
      element.removeAttribute('href');
    }

    if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName) && !element.hasAttribute('disabled')) {
      element.setAttribute('data-a11y-disabled-by-script', 'true');
      element.setAttribute('disabled', 'disabled');
    }
  }

  restoreFocusableElement(element) {
    if (element.hasAttribute('data-a11y-orig-tabindex')) {
      const originalTabindex = element.getAttribute('data-a11y-orig-tabindex');
      if (originalTabindex === '') {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', originalTabindex);
      }
      element.removeAttribute('data-a11y-orig-tabindex');
    }

    if (element.hasAttribute('data-a11y-orig-href')) {
      element.setAttribute('href', element.getAttribute('data-a11y-orig-href'));
      element.removeAttribute('data-a11y-orig-href');
    }

    if (element.hasAttribute('data-a11y-disabled-by-script')) {
      element.removeAttribute('disabled');
      element.removeAttribute('data-a11y-disabled-by-script');
    }
  }

  reloadSection() {
    this.removeCarouselA11yListeners();

    // Reset a11y modifications on the DOM directly, even if Flickity is gone
    this.querySelectorAll('[data-a11y-orig-tabindex], [data-a11y-orig-href], [data-a11y-disabled-by-script]').forEach((el) => {
      this.restoreFocusableElement(el);
    });

    if(this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy();
    }

    const mql = window.matchMedia("screen and (max-width: 989px)");

    if(mql.matches && this.dataset.carousel !== undefined) {
      this.querySelectorAll('.carousel__item').forEach(el => {
        el.classList.add('carousel__item');
        el.classList.remove('grid__item');
      });

      this.querySelectorAll('.product-grid').forEach(el => {
        el.classList.add('carousel');
        el.classList.remove('grid', 'grid--2-col', 'grid--3-col-tablet', 'grid--6-col-desktop');
      });

      this.reloadCarousel();
    } else {
      this.querySelectorAll('.carousel__item').forEach(el => {
        el.classList.add('grid__item');
        el.classList.remove('carousel__item');
      });

      this.querySelectorAll('.product-grid').forEach(el => {
        el.classList.add('grid', 'grid--2-col', 'grid--3-col-tablet', 'grid--6-col-desktop');
        el.classList.remove('carousel');
      });
    }
    
    setTimeout(() => {
      var blockHeight = this.querySelector('.tab-collage__item.is-active > *').clientHeight;
      this.style.setProperty('--active-block-height', blockHeight + 'px');
    }, 40);
  }

  pauseAllMedia() {
    this.querySelectorAll('video-section[data-type="youtube"] iframe').forEach((video) => {
      video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    });
    this.querySelectorAll('video-section[data-type="vimeo"] iframe').forEach((video) => {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    this.querySelectorAll('video').forEach((video) => video.pause());
    document.querySelectorAll('video').forEach((video) => {
      if (!video.closest('[data-video-hero]') && !video.closest('.ugc-card')) video.pause();
    });
  }
}
customElements.define('tab-collage', TabCollage);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.parent = this.closest('.product-countdown');
    this.date = this.parseToDate(this.dataset.date).getTime();
    
    if (isNaN(this.date)) {
      this.unload();
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }
  
  parseToDate(input) {
    if (input.includes("T")) {
      return new Date(input);
    }
    
    const [date, time] = input.split(' ');
    const [month, day, year] = date.split('-');
    return new Date(`${year}-${month}-${day} ${ time || '12:00AM' }`);
  }

  init() {
    this.timer();
    
    if(JSON.parse(this.dataset.paused)) return;
    this.dataset.interval = setInterval(this.timer.bind(this), 1000);
  }

  timer() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = (countTo - now);

    if (timeDifference < 0) {
      this.unload();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    let days = Math.floor(timeDifference / (secondsInADay) * 1);
    let hours = Math.floor((timeDifference % (secondsInADay)) / (secondsInAHour) * 1);
    const mins = Math.floor(((timeDifference % (secondsInADay)) % (secondsInAHour)) / (60 * 1000) * 1);
    const secs = Math.floor((((timeDifference % (secondsInADay)) % (secondsInAHour)) % (60 * 1000)) / 1000 * 1);

    // Convert days into hours if days left is less than dataset.days
    if(days < parseInt(this.dataset.minDays)) {
      hours = hours + (days * 24);
      days = 0;
    }

    if (this.dataset.compact == 'true') {
      const dayHTML = days > 0 ? `<div class="countdown__item"><span>${days}${theme.dateStrings.d}</span></div>` : '';
      const otherHTML = `<div class="countdown__item"><span>${hours > 9 ? hours : '0' + hours}:${mins > 9 ? mins : '0' + mins}:${secs > 9 ? secs : '0' + secs}</span></div>`;
  
      this.innerHTML = dayHTML + otherHTML;
    }
    else {
      const dayHTML = days > 0 ? `<div class="countdown__item"><span>${days}</span> ${days == 1 ? theme.dateStrings.day : theme.dateStrings.days}</div>` : '';
      const hourHTML = `<div class="countdown__item"><span>${hours}</span> ${hours == 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</div>`;
      const minHTML = `<div class="countdown__item"><span>${mins}</span> ${mins == 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</div>`;
      const secHTML = `<div class="countdown__item"><span>${secs}</span> ${secs == 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
  }

  unload() {
    if (this.dataset.interval) {
      clearInterval(this.dataset.interval);
    }

    this.classList.add('hidden');
    if (this.parent) {
      this.parent.classList.add('hidden');
    }
  }
}
customElements.define('countdown-timer', CountdownTimer);

if (!customElements.get('boxed-countdown')) {
  customElements.define('boxed-countdown', class extends HTMLElement {
    connectedCallback() {
      this.stop();
      const epoch = this.dataset.endEpoch;
      if (epoch === undefined) {
        this.deadline = Date.parse(this.dataset.endDate);
      } else {
        this.deadline = /^-?\d+$/.test(epoch) ? Number(epoch) * 1000 : NaN;
      }
      this.numbers = Array.from(this.querySelectorAll('[data-unit]'));
      this.hidden = !Number.isFinite(this.deadline);

      if (!this.hidden && this.update()) {
        this.interval = setInterval(() => this.update(), 1000);
      }
    }

    disconnectedCallback() {
      this.stop();
    }

    stop() {
      clearInterval(this.interval);
      this.interval = null;
    }

    update() {
      const remaining = Math.max(0, this.deadline - Date.now());
      const values = {
        days: Math.floor(remaining / 86400000),
        hours: Math.floor(remaining / 3600000) % 24,
        minutes: Math.floor(remaining / 60000) % 60,
        seconds: Math.floor(remaining / 1000) % 60,
      };

      this.numbers.forEach((number) => {
        const value = String(values[number.dataset.unit]).padStart(2, '0');
        if (number.textContent !== value) number.textContent = value;
      });

      if (remaining === 0) {
        this.hidden = true;
        this.stop();
      }
      return remaining > 0;
    }
  });
}

// Inline countdown for promo end dates (product promo box + announcement bar).
// Counts down to an absolute unix epoch (timezone-safe), then reverts to the
// static "Ends dd.mm.yy" fallback. See snippets/promo-countdown.liquid.
class PromoCountdown extends HTMLElement {
  connectedCallback() {
    const epoch = parseInt(this.dataset.epoch, 10);
    if (!epoch) return;

    this.endMs = epoch * 1000;
    // Do not name this `prefix`: Element.prototype.prefix is a read-only DOM
    // property, so assigning to it throws in strict mode and aborts this method.
    this.labelPrefix = this.dataset.prefix || '';
    this.fallback = this.dataset.fallback || this.textContent;
    this.hideFallback = this.hasAttribute('data-hide-fallback');
    this.cards = this.dataset.variant === 'cards';
    this.hhmmWithinMs = (parseFloat(this.dataset.hhmmHours) || 0) * 3600000;
    this.secondsWithinMs = (parseFloat(this.dataset.secondsHours) || 0) * 3600000;

    if (this.cards) {
      this.classList.add('promo-countdown--cards');
    }

    if (this.interval) clearInterval(this.interval);
    // Only start ticking if the promo has not already ended (e.g. a cached page).
    if (this.render()) {
      this.interval = setInterval(this.render.bind(this), 1000);
    }
  }

  disconnectedCallback() {
    if (this.interval) clearInterval(this.interval);
  }

  pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  escapeHtml(str) {
    return str.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  render() {
    const diff = this.endMs - Date.now();

    if (diff <= 0) {
      if (this.interval) clearInterval(this.interval);
      // Callers that opt out of the static fallback (e.g. offerbox) drop the badge entirely on expiry.
      if (this.hideFallback) {
        this.remove();
      } else {
        this.textContent = this.fallback;
      }
      return false;
    }

    const total = Math.floor(diff / 1000);
    // Tiers by time left: days only -> HH:MM (within hhmm window) -> HH:MM:SS (within seconds window).
    let tier;
    let parts;
    if (this.secondsWithinMs && diff <= this.secondsWithinMs) {
      tier = 'hms';
      parts = [
        { value: Math.floor(total / 3600), label: 'hrs' },
        { value: Math.floor((total % 3600) / 60), label: 'min' },
        { value: total % 60, label: 'sec' }
      ];
      // Drop leading zero units: the final hour reads mm:ss, the final minute reads ss.
      while (parts.length > 1 && parts[0].value === 0) {
        parts.shift();
      }
    } else if (this.hhmmWithinMs && diff <= this.hhmmWithinMs) {
      tier = 'hm';
      parts = [
        { value: Math.floor(total / 3600), label: 'hrs' },
        { value: Math.floor((total % 3600) / 60), label: 'min' }
      ];
    } else {
      tier = 'days';
      const days = Math.floor(total / 86400);
      parts = [{ value: days, label: days === 1 ? 'day' : 'days', isDays: true }];
    }

    if (this.cards) {
      // Card styling lives in assets/component-promo-countdown.scss; keep class names in sync.
      const cards = parts
        .map((p) => {
          const num = p.isDays ? p.value : this.pad2(p.value);
          return `<span class="promo-countdown__unit"><span class="promo-countdown__num">${num}</span><span>${p.label}</span></span>`;
        })
        .join('');
      const safePre = this.escapeHtml(this.labelPrefix);
      const pre = safePre ? `<span class="promo-countdown__prefix">${safePre}</span>` : '';
      this.innerHTML = pre + cards;
    } else if (tier === 'days') {
      this.textContent = this.labelPrefix + `${parts[0].value} ${parts[0].label}`;
    } else {
      // hm/hms tiers: e.g. "3h 12m" / "1h 23m 45s" — first letter of each part's label.
      // Zero hours/minutes are dropped ("49h 39s", "3h", "45m"); seconds always show so the
      // ticker doesn't change width every minute. Keep at least one unit.
      const shown = parts.filter((p) => p.value !== 0 || p.label === 'sec');
      this.textContent =
        this.labelPrefix +
        (shown.length ? shown : parts.slice(-1))
          .map((p) => `${p.value}${p.label[0]}`)
          .join(' ');
    }
    return true;
  }
}
customElements.define('promo-countdown', PromoCountdown);

class GMap extends HTMLElement {
  constructor() {
    super();

    if (!this.dataset.apiKey || !this.dataset.mapAddress) {
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.prepMapApi.bind(this),
      threshold: 200
    });
  }

  prepMapApi() {
    this.loadScript().then(this.initMap.bind(this));
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + this.dataset.apiKey;
    });
  }

  initMap() {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: this.dataset.mapAddress }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {
        if (Shopify.designMode) {}
      }
      else {
        const mapOptions = {
          zoom: parseInt(this.dataset.zoom),
          center: results[0].geometry.location,
          draggable: true,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true
        };

        const map = new google.maps.Map(this, mapOptions), center = map.getCenter();

        map.setCenter(center);

        const icon = {
          path: "M32.7374478,5.617 C29.1154478,1.995 24.2994478,0 19.1774478,0 C14.0544478,0 9.23944778,1.995 5.61744778,5.617 C-1.08555222,12.319 -1.91855222,24.929 3.81344778,32.569 L19.1774478,54.757 L34.5184478,32.6 C40.2734478,24.929 39.4404478,12.319 32.7374478,5.617 Z M19.3544478,26 C15.4954478,26 12.3544478,22.859 12.3544478,19 C12.3544478,15.141 15.4954478,12 19.3544478,12 C23.2134478,12 26.3544478,15.141 26.3544478,19 C26.3544478,22.859 23.2134478,26 19.3544478,26 Z",
          fillColor: this.dataset.markerColor,
          fillOpacity: 1,
          anchor: new google.maps.Point(15, 55),
          strokeWeight: 0,
          scale: 0.6
        };

        new google.maps.Marker({
          map: map,
          position: map.getCenter(),
          icon: icon
        });

        const styledMapType = new google.maps.StyledMapType(JSON.parse(this.parentNode.querySelector('[data-gmap-style]').innerHTML));

        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');

        google.maps.event.addDomListener(window, 'resize', function () {
          google.maps.event.trigger(map, 'resize');
          map.setCenter(center);
        });
      }
    });
  }
}
customElements.define('g-map', GMap);

class RelatedButtons extends HTMLElement {
  constructor() {
    super();

    if(!document.querySelector(this.dataset.scrollto)) {
      this.classList.add('hidden');
      return;
    }

    window.addEventListener('scroll', this.checkListener.bind(this));
    this.querySelectorAll('a').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  checkListener() {
    const element = document.querySelector(this.dataset.scrollto);
    const show = window.scrollY >= element.offsetTop - element.clientHeight;

    if (show) {
      this.classList.add('is-flipped');
    } else {
      this.classList.remove('is-flipped');
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.target;
    const scrollIntoView = document.querySelector(target.getAttribute('href'));

    scrollIntoView.scrollIntoView({behavior: "smooth"});
  }
}
customElements.define('related-buttons', RelatedButtons);

class ProductRecentlyViewed extends HTMLElement {
  constructor() {
    super();
    
    // We save the product ID in local storage to be eventually used for recently viewed section
    if (isStorageSupported('local')) {
      const productId = parseInt(this.dataset.productId);
      const cookieName = 'beyours:recently-viewed';
      const items = JSON.parse(window.localStorage.getItem(cookieName) || '[]');

      // We check if the current product already exists, and if it does not, we add it at the start
      if (!items.includes(productId)) {
        items.unshift(productId);
      }

      // By keeping only the 4 most recent
      window.localStorage.setItem(cookieName, JSON.stringify(items.slice(0, 5)));
    }
  }
}
customElements.define('product-recently-viewed', ProductRecentlyViewed);

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    fetch(this.dataset.url + this.getQueryString())
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('recently-viewed-products');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  getQueryString() {
    const cookieName = 'beyours:recently-viewed';
    const items = JSON.parse(window.localStorage.getItem(cookieName) || "[]");
    if (this.dataset.productId && items.includes(parseInt(this.dataset.productId))) {
      items.splice(items.indexOf(parseInt(this.dataset.productId)), 1);
    }
    return items.map((item) => "id:" + item).slice(0, 4).join(" OR ");
  }
}
customElements.define('recently-viewed-products', RecentlyViewedProducts);

class VideoSection extends HTMLElement {
  constructor() {
    super();

    this.background = this.dataset.initMode !== 'template';
    this.loop = this.dataset.loop;

    if (this.background) {
      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600
      });
    }
    else {
      this.init();
    }
  }

  init() {
    this.parentSelector = this.dataset.parent || '.media-wrapper';
    this.parent = this.closest(this.parentSelector);

    switch(this.dataset.type) {
      case 'youtube':
        this.initYoutubeVideo();
        break;

      case 'vimeo':
        this.initVimeoVideo();
        break;

      case 'mp4':
        this.initMp4Video();
        break;
    }
  }

  initYoutubeVideo() {
    this.setAsLoading();
    this.loadScript('youtube').then(this.setupYoutubePlayer.bind(this));
  }

  initVimeoVideo() {
    this.setAsLoading();
    this.loadScript('vimeo').then(this.setupVimeoPlayer.bind(this));
  }

  initMp4Video() {
    const player = this.querySelector('video');

    if (player) {
      const promise = player.play();

      // Edge does not return a promise (video still plays)
      if (typeof promise !== 'undefined') {
        promise.then(function() {
          // playback normal
        }).catch(function() {
          player.setAttribute('controls', '');
        });
      }
    }
  }

  loadScript(videoType) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = videoType === 'youtube' ? '//www.youtube.com/iframe_api' : '//player.vimeo.com/api/player.js';
    });
  }

  setAsLoading() {
    this.parent.setAttribute('loading', true);
  }

  setAsLoaded() {
    this.parent.removeAttribute('loading');
    this.parent.setAttribute('loaded', true);
  }

  setupYoutubePlayer() {
    const videoId = this.dataset.videoId;
    
    const playerInterval = setInterval(() => {
      if (window.YT) {
        window.YT.ready(() => {
          const element = document.createElement('div');
          this.appendChild(element);

          this.player = new YT.Player(element, {
            videoId: videoId,
            playerVars: {
              showinfo: 0,
              controls: !this.background,
              fs: !this.background,
              rel: 0,
              height: '100%',
              width: '100%',
              iv_load_policy: 3,
              html5: 1,
              loop: this.loop == '1' ? 1 : 0,
              playlist: videoId,
              playsinline: 1,
              modestbranding: 1,
              disablekb: 1
            },
            events: {
              onReady: this.onYoutubeReady.bind(this),
              onStateChange: this.onYoutubeStateChange.bind(this)
            }
          });
          clearInterval(playerInterval);
        });
      }
    }, 50);
  }

  onYoutubeReady() {
    this.iframe = this.querySelector('iframe'); // iframe once YT loads
    this.iframe.setAttribute('tabindex', '-1');

    this.background && this.player.mute();
    if (typeof this.player.playVideo === 'function') this.player.playVideo();

    this.setAsLoaded();

    // pause when out of view
    const observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        entry.isIntersecting ? this.youtubePlay() : this.youtubePause();
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    observer.observe(this.iframe);
  }

  onYoutubeStateChange(event) {
    switch (event.data) {
      case -1: // unstarted
        // Handle low power state on iOS by checking if
        // video is reset to unplayed after attempting to buffer
        if (this.attemptedToPlay) {
          this.setAsLoaded();
          this.closest('.banner').classList.add('video-interactable');
        }
        break;
      case 0: // ended, loop it
        this.youtubePlay();
        break;
      case 1: // playing
        this.setAsLoaded();
        break;
      case 3: // buffering
        this.attemptedToPlay = true;
        break;
    }
  }

  youtubePlay() {
    if (this.background && this.player && typeof this.player.playVideo === 'function') {
      this.player.playVideo();
    }
  }

  youtubePause() {
    if (this.background && this.player && typeof this.player.pauseVideo === 'function') {
      this.player.pauseVideo();
    }
  }

  setupVimeoPlayer() {
    const videoId = this.dataset.videoId;

    const playerInterval = setInterval(() => {
      if (window.Vimeo) {
        this.player = new Vimeo.Player(this, {
          id: videoId,
          autoplay: true,
          autopause: false,
          background: this.background,
          controls: !this.background,
          loop: this.loop == '1' ? true : false,
          playlist: videoId,
          height: '100%',
          width: '100%'
        });
        this.player.ready().then(this.onVimeoReady.bind(this));

        clearInterval(playerInterval);
      }
    }, 50);
  }

  onVimeoReady() {
    this.iframe = this.querySelector('iframe');
    this.iframe.setAttribute('tabindex', '-1');

    this.background && this.player.setMuted(true);

    this.setAsLoaded();

    // pause when out of view
    const observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.vimeoPlay();
        } else {
          this.vimeoPause();
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    observer.observe(this.iframe);
  }

  vimeoPlay() {
    if (this.background && this.player && typeof this.player.play === 'function') {
      this.player.play();
    }
  }

  vimeoPause() {
    if (this.background && this.player && typeof this.player.pause === 'function') {
      this.player.pause();
    }
  }
}
customElements.define('video-section', VideoSection);

class BundleProduct extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      souldout: 'price--sold-out',
      onsale: 'price--on-sale',
      nocompare: 'price--no-compare'
    };

    this.addToCart = this.querySelector('add-to-cart');
    this.price = this.querySelector('.price');
    this.variants = this.querySelector('select');
    if (this.variants) {
      this.variants.addEventListener('change', this.onSelectChange.bind(this));
    }
  }

  onSelectChange(event) {
    const variants = event.target;
    const variant = variants.options[variants.selectedIndex];
    const compare_at_price = parseInt(variant.dataset.compare_at_price || 0);
    const price = parseInt(variant.dataset.price || 0);
    const available = variant.dataset.available === 'true';
    const price_varies = variants.dataset.price_varies === 'true';
    const compare_at_price_varies = variants.dataset.compare_at_price_varies === 'true';

    // Remove classes
    this.price.classList.remove(this.classes.souldout);
    this.price.classList.remove(this.classes.onsale);
    this.price.classList.remove(this.classes.nocompare);

    // Add classes
    if (available === false) {
      this.price.classList.add(this.classes.souldout);
    }
    else if (compare_at_price > price && available) {
      this.price.classList.add(this.classes.onsale);
    }
    if (price_varies == false && compare_at_price_varies) {
      this.price.classList.add(this.classes.nocompare);
    }

    // Change price
    const price__regular = this.querySelector('.price__regular');
    price__regular.querySelector('.price-item--regular').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;

    const price__sale = this.querySelector('.price__sale');
    price__sale.querySelector('.price-item--regular').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(compare_at_price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;
    price__sale.querySelector('.price-item--sale').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(price, theme.shopSettings.moneyFormat)}</bdi></price-money>`;

    // Change variant id
    if (this.addToCart) {
      this.addToCart.dataset.variantId = variant.value;
    }
  }
}
customElements.define('bundle-product', BundleProduct);

class BundleProducts extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
    };

    this.miniCart = document.querySelector('mini-cart');
    this.button = this.querySelector('button');
    if (this.button) this.button.addEventListener('click', this.onButtonClick.bind(this));

    this.blocks = this.querySelectorAll('[data-block-id]');
    this.blocks.forEach((block) => block.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    this.blocks.forEach((block) => block.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));
  }

  onEnterHandler(event) {
    this.classList.add(this.classes.hover);

    const target = event.target;
    const blockId = target.dataset.blockId;
    this.blocks.forEach((block) => {
      if (target.nodeName === 'BUNDLE-PRODUCT') {
        if (block.nodeName === 'BUNDLE-PRODUCT') {
          block.classList.add(this.classes.active);
          return;
        }
      }
      if (block.dataset.blockId === blockId) {
        block.classList.add(this.classes.active);
      }
    });
  }

  onLeaveHandler() {
    this.classList.remove(this.classes.hover);
    this.blocks.forEach((block) => block.classList.remove(this.classes.active));
  }

  onButtonClick(event) {
    event.preventDefault();

    const ids = this.querySelectorAll('[name="id"]'),
          bundleProperty = {
            "__isbundle": 'true',
            "__product": this.dataset.bundleHandle,
            "__discount": this.dataset.bundleDiscount,
            "__components": this.dataset.bundleComponents,
            "__bundleimg": this.dataset.bundleImage,
            "__bundletitle": this.dataset.bundleTitle,
            "__bundleoriginalprice": this.dataset.bundleOriginalPrice,
            "__bundlefinalprice": this.dataset.bundleFinalPrice,
            "__bundleaddid": Math.floor(100000 + Math.random() * 900000),
            "__bundleallowpromos": this.dataset.bundleAllowPromos,
            "__atc_source": "bundle"
          };
    const items = {
      items: [...ids].map((e => e.getAttribute('data-selected-variant-id'))).filter(e => e != '').map((e => ({
        id: e,
        quantity: 1,
        properties: bundleProperty
      })))
    };

    if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
      Shopify.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...items
        }
      });
      return;
    }

    this.handleErrorMessage();

    this.button.setAttribute('disabled', true);
    this.button.classList.add('loading');
    const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.section || section.id) : [];

    const body = JSON.stringify({
      ...items,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        if (response.status === 422) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: response.description
            }
          }));
        }
        else {
          this.miniCart && this.miniCart.renderContents(response);

          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: response
            }
          }));
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    
    if (this.errorMessageWrapper) {
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
    else {
      if (errorMessage) {
        alert(errorMessage);
      }
    }
  }
}
customElements.define('bundle-products', BundleProducts);

class BundleComponent extends BundleProducts {
  constructor() {
    super();
    
    this.parentProduct = this.parentElement.dataset;
    this.product = this.dataset;
    this.variantData = JSON.parse(this.querySelector('[type="application/json"]').textContent);

    this.updateSelectedVariant();

    this.querySelectorAll('select[class$="-options"]').forEach((selectElement) => {
      if(selectElement.options.length > 1) {
        let parentDiv = selectElement.parentElement;

        if(parentDiv) {
          parentDiv.classList.remove('hidden');
        }
      }

      selectElement.addEventListener('change', this.updateSelectedVariant.bind(this));
    });
  }
   
  updateSelectedVariant() {
    const selects = Array.from(this.querySelectorAll(`.${ this.product.handle }-options`));
    const selectedOptions = selects.map(select => select.value);

    let selectedVariant;
    let selectedVariantId;
    
    for (const id in this.variantData) {
      const variant = this.variantData[id];
      const variantOptions = variant.title.split(' / ').slice(0, selectedOptions.length); // Only compare as many options as there are in the DOM
      
      if (JSON.stringify(variantOptions) === JSON.stringify(selectedOptions)) {
        selectedVariantId = id;
        selectedVariant = variant;
        break;
      }
    }
    
    if(selectedVariant){
      // if the variant is on sale, we use the compare price, not the sale price
      const priceToUse = selectedVariant.comparePrice != 0 ? selectedVariant.comparePrice : selectedVariant.price;
      const priceElement = this.querySelector(`.variant-price-${ this.product.handle }`);

      if(priceElement) {
        priceElement.innerText = this.formatPrice(priceToUse);
      }
      
      const optionsElement = this.querySelector(`.${ this.product.handle }-options`);

      if(optionsElement) {
        optionsElement.setAttribute('data-selected-variant-id', selectedVariantId);
      }

      if(selectedVariant.numberOfServes){
        const numOfServes = this.querySelector(`[data-num-of-serves="${ this.product.handle }"]`);

        if(numOfServes){
          numOfServes.classList.remove('hidden');
          const serves = String(selectedVariant.numberOfServes).trim();
          numOfServes.querySelector('span').innerText = serves === '1' ? `${serves} serve` : `${serves} serves`;
        }
      }

      const addToCartButton = this.parentElement.querySelector('button[name="add"]');
      const formErrorWrapper = this.parentElement.querySelector('.product-form__error-message-wrapper');

      if( ! selectedVariant.available) {
        this.querySelector('.bpw-error').classList.remove('hidden');
        this.querySelector('.bpw-error').querySelector('span').innerText = window.languages.products.product.sold_out;
        
        if(addToCartButton) {
          addToCartButton.setAttribute('disabled', 'disabled');
          addToCartButton.innerText = window.languages.products.product.unavailable;
        }

        if(formErrorWrapper) {
          formErrorWrapper.removeAttribute('hidden');
          formErrorWrapper.querySelector('.product-form__error-message').innerText = window.languages.products.bundle.unavailable;
        }
      }

      const imageElement = this.querySelector('img');
      if(imageElement && selectedVariant.image){
        imageElement.src = selectedVariant.image;
      }

      if(selectedVariant.price){
        this.variantData[selectedVariantId].selectedPrice = parseFloat(priceToUse);
        this.setAttribute('data-selected-price', parseFloat(priceToUse));
        this.updateGlobalPrice();
      }

      const selectedVariantNutritionalTable = document.querySelector(`.nutritional-table-wrap[data-variant-id="${ selectedVariantId }"][data-handle="${ this.product.handle }"]`);
      
      if(selectedVariantNutritionalTable){
        const currentlyVisibleNutritionalTable = document.querySelector(`.nutritional-table-wrap[data-handle="${ this.product.handle }"]:not([data-variant-id="${ selectedVariantId }"])`);
        
        if(currentlyVisibleNutritionalTable){
          currentlyVisibleNutritionalTable.classList.add('hidden');
        }

        selectedVariantNutritionalTable.classList.remove('hidden');
      }
    }
  }

  formatPrice(price) {
    if(typeof price !== 'string'){
      price = price.toString();
    }

    return `$${parseFloat(price).toFixed(2)}`;
  }

  updateGlobalPrice() {
    let globalPrice = 0;

    this.parentElement.querySelectorAll('bundle-component').forEach((product) => {
      const variantData = JSON.parse(product.querySelector('[type="application/json"]').textContent);
      globalPrice += parseFloat(product.dataset.selectedPrice);

      for(const id in variantData) {
        const thisVariant = variantData[id];

        if( ! thisVariant.available){
          const selectTitleFromVariantTitle = thisVariant.title.substr(0, thisVariant.title.indexOf(' /'));
          const matchingSelectOption = document.querySelector(`select[data-id="${ product.dataset.id }"] option[value="${ selectTitleFromVariantTitle }"]`);
          
          if(matchingSelectOption){
            if(matchingSelectOption.innerText.indexOf('out of stock') === -1){
              matchingSelectOption.innerText += ' (out of stock)';
            }

            matchingSelectOption.setAttribute('disabled', 'disabled');
          }
        }
      }
    });

    const discountPercent = parseFloat(this.parentProduct.bundleDiscount);
    let discountedPrice = globalPrice - (globalPrice * (discountPercent / 100));
    let amountSaved = globalPrice - discountedPrice;
    let afterpayAmount = discountedPrice / 4;
    globalPrice = this.formatPrice(globalPrice.toFixed(2));
    discountedPrice = this.formatPrice(discountedPrice.toFixed(2));
    afterpayAmount = this.formatPrice(afterpayAmount.toFixed(2));
    amountSaved = this.formatPrice(amountSaved.toFixed(2));
    this.closest('product-main').querySelector('.afterpay__line2 strong').innerText = afterpayAmount;
    this.closest('product-main').querySelector('.product__info-wrapper .price-item--sale bdi').innerText = discountedPrice;
    this.closest('product-main').querySelector('.product__info-wrapper .price-item--regular bdi').innerText = globalPrice;

    const youSaveBadge = this.closest('product-main').querySelector('.price__badge-you-save');
    if (youSaveBadge) youSaveBadge.textContent = 'Save ' + amountSaved;
  }
}
customElements.define('bundle-component', BundleComponent);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.blockCount == 1) {
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    this.products = this.querySelector('.image-with-text__products');

    this.blocks = this.querySelectorAll('lookbook-component [data-block-index]');
    this.blocks.forEach((block) => block.addEventListener('mouseenter', this.onEnterHandler.bind(this)));

    this.initSlider();
  }

  onEnterHandler(event) {
    if (this.flickity && typeof this.flickity.select === 'function') {
      const index = parseInt(event.target.dataset.blockIndex);
      this.flickity.select(index);
    }
  }

  onFocusHandler(index) {
    this.blocks.forEach((block) => {
      block.classList.remove('focus');

      if (parseInt(block.dataset.blockIndex) === index) {
        block.classList.add('focus');
      }
    });
  }

  initSlider() {
    this.flickity = new Flickity(this.products, {
      accessibility: false,
      rightToLeft: theme.config.rtl,
      prevNextButtons: false,
      pageDots: true,
      wrapAround: true,
      fade: true,
      setGallerySize: true,
      on: {
        ready: () => {
          this.onFocusHandler(0);
        },
        change: (index) => {
          this.onFocusHandler(index);
        }
      }    
    });
  }
}
customElements.define('shop-the-look', ShopTheLook);

class SelectWrapper extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });

    this.select = this.querySelector('select');
    if (this.select) {
      this.select.addEventListener('change', this.handleSelectChange.bind(this));
    }
  }

  init() {
    const style = window.getComputedStyle(this.select);
    const value = this.select.options[this.select.selectedIndex].text;

    const text = document.createElement('span');
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = 'hidden';
    text.style.position = 'absolute';
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.clientWidth;
    
    this.style.setProperty('--width', `${width}px`);
    text.remove();
  }

  handleSelectChange() {
    this.init();
  }
}
customElements.define('select-wrapper', SelectWrapper);

class ImageComparison extends HTMLElement {
  constructor() {
    super();

    this.active = false;
    this.button = this.querySelector('button');
    this.horizontal = this.dataset.layout === 'horizontal';
    this.bodyStyleOverflowY = document.body.style.overflowY;
    this.init();

    theme.initWhenVisible({
      element: this.querySelector('.image-comparison__animate'),
      callback: this.animate.bind(this),
      threshold: 0
    });
  }

  animate() {
    this.setAttribute('animate', '');
    this.classList.add('animating');
    setTimeout(() => {
      this.classList.remove('animating');
    }, 1e3);
  }

  init() {
    this.button.addEventListener('touchstart', this.startHandler.bind(this));
    document.body.addEventListener('touchend', this.endHandler.bind(this));
    document.body.addEventListener('touchmove', this.onHandler.bind(this));
    
    this.button.addEventListener('mousedown', this.startHandler.bind(this));
    document.body.addEventListener('mouseup', this.endHandler.bind(this));
    document.body.addEventListener('mousemove', this.onHandler.bind(this));
  }

  startHandler() {
    if(window.innerWidth <= 750) {
      document.body.style.overflowY = 'hidden';
    }

    this.active = true;
    this.classList.add('scrolling');
  }

  endHandler() {
    this.active = false;
    this.classList.remove('scrolling');
    document.body.style.overflowY = this.bodyStyleOverflowY;
  }

  onHandler(e) {
    if (!this.active) return;
    
    const event = (e.touches && e.touches[0]) || e;
    const x = this.horizontal
                ? event.pageX - this.offsetLeft
                : event.pageY - this.offsetTop;

    this.scrollIt(x);
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;
    
    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, (Math.min(x, max)));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty('--percent', mousePercent + '%');
  }
}
customElements.define('image-comparison', ImageComparison);

class ScrollingPromotion extends HTMLElement {
  constructor() {
    super();

    this.config = {
      moveTime: parseFloat(this.dataset.speed), // 100px going to move for
      space: 100, // 100px
    };

    this.promotion = this.querySelector('.promotion');

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    if (this.childElementCount === 1) {
      this.promotion.classList.add('promotion--animated');

      for (let index = 0; index < 10; index++) {
        this.clone = this.promotion.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.neutralizeCloneFocusableElements(this.clone);
        this.appendChild(this.clone);

        const imageWrapper = this.clone.querySelector('.media-wrapper');
        if (imageWrapper) {
          imageWrapper.classList.remove('loading');
        }
      }

      const animationTimeFrame = (this.promotion.clientWidth / this.config.space) * this.config.moveTime;
      this.style.setProperty('--duration', `${animationTimeFrame}s`);

      // pause when out of view
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.scrollingPlay();
          } else {
            this.scrollingPause();
          }
        });
      }, {rootMargin: '0px 0px 50px 0px'});

      observer.observe(this);
    }
  }

  neutralizeCloneFocusableElements(clone) {
    const focusableSelector = 'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    clone.querySelectorAll(focusableSelector).forEach((el) => {
      el.setAttribute('tabindex', '-1');

      if ((el.tagName === 'A' || el.tagName === 'AREA') && el.hasAttribute('href')) {
        el.removeAttribute('href');
      }

      if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName) && !el.hasAttribute('disabled')) {
        el.setAttribute('disabled', 'disabled');
      }
    });
  }

  scrollingPlay() {
    this.classList.remove('scrolling-promotion--paused');
  }

  scrollingPause() {
    this.classList.add('scrolling-promotion--paused');
  }
}
customElements.define('scrolling-promotion', ScrollingPromotion);

class AccordionTab extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.accordion__content');

    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200
    });
  }

  init() {
    this.summary.addEventListener('click', (e) => this.onClick(e));
  }

  onClick(e) {
    e.preventDefault();

    this.details.style.overflow = 'hidden';
    if (this.isClosing || !this.details.open) {
      const wasClosed = !this.details.open;
      this.open();
      if (wasClosed) {
        this.dispatchEvent(new CustomEvent('accordion:open', {
          bubbles: true,
          detail: { heading: this.summary, clickEvent: e }
        }));
      }
    }
    else if (this.isExpanding || this.details.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;
    
    const summaryStyle = window.getComputedStyle(this.summary);
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop)}px`;
    
    if (this.animation) {
      this.animation.cancel();
    }
    
    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 250,
      easing: 'ease'
    });
    
    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => this.isClosing = false;
  }

  open() {
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.details.open = true;
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;

    const summaryStyle = window.getComputedStyle(this.summary);
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop) + this.content.offsetHeight}px`;
    
    if (this.animation) {
      this.animation.cancel();
    }
    
    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 400,
      easing: 'ease-out'
    });
    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => this.isExpanding = false;
  }

  onAnimationFinish(open) {
    this.details.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.style.height = this.details.style.overflow = '';
  }
}
customElements.define('accordion-tab', AccordionTab);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    new IntersectionObserver(this.handleIntersection.bind(this), {rootMargin: '0px 0px 600px 0px'}).observe(this);
  }

  async handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    if (this.dataset.recommendationsSource == 'metafield') return;

    await fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class HeaderMenuToggle extends HTMLElement {
  constructor() {
    super();
    
    this.header = document.querySelector('.shopify-section-header');
    this.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick() {
    this.header.classList.toggle('shopify-section-header-menu-open');
  }
}
customElements.define('header-menu-toggle', HeaderMenuToggle);

class OptionTable extends HTMLElement {
  constructor() {
    super();

    this.config = {
    };

    this.imageOptionRows = this.querySelectorAll(".ot--images .ot--option-row");
    this.dataOptionRows = this.querySelectorAll(".ot--data .ot--option-row");

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600
    });
  }

  init() {
    const optionTable = this;

    this.imageOptionRows.forEach((imageOptionRow) => {
      imageOptionRow.addEventListener("mouseenter", function () {
        optionTable.querySelectorAll(".ot--data .ot--option-row").forEach((el) => {
          el.classList.remove("active");
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.opacity = "0.3";
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.zIndex = "initial";
        });

        this.style.opacity = "1";
        this.style.zIndex = "3";

        var imageRowId = this.id;
        var dataRowId = imageRowId.replace("imageRow", "dataRow");
        var activeDataRow = optionTable.querySelector(`#${dataRowId}`);
        activeDataRow.classList.add("active");

        optionTable.querySelectorAll(`:scope .data-row.data-headers>div:not(.blank)`).forEach(el => {
          el.classList.remove('active');
        });
        var activeDataRowValues = activeDataRow.querySelectorAll(`:scope div:not(.is-header)`);
        activeDataRowValues.forEach((activeDataRowValue, i) => {
          if (activeDataRowValue.querySelectorAll(`:scope svg.checkmark`).length > 0) {
            optionTable.querySelectorAll(`:scope .data-row.data-headers>div:not(.blank)`)[i].classList.add('active');
          }
        });
      });
      imageOptionRow.addEventListener("mouseleave", function () {
        optionTable.querySelectorAll(".ot--data .ot--option-row").forEach((el) => {
          el.classList.remove("active");
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.opacity = "1";
        });
        optionTable.querySelectorAll(`:scope .data-row.data-headers>div:not(.blank)`).forEach(el => {
          el.classList.remove('active');
        });
      });
    });

    this.dataOptionRows.forEach((dataOptionRow) => {
      dataOptionRow.addEventListener("mouseenter", function () {
        optionTable.querySelectorAll(".ot--data .ot--option-row").forEach((el) => {
          el.classList.remove("active");
        });
        this.classList.add("active");
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.opacity = "0.3";
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.zIndex = "initial";
        });

        var dataRowId = this.id;
        var imageRowId = dataRowId.replace("dataRow", "imageRow");
        optionTable.querySelector(`#${imageRowId}`).style.opacity = "1";
        optionTable.querySelector(`#${imageRowId}`).style.zIndex = "3";
      });
      dataOptionRow.addEventListener("mouseleave", function () {
        optionTable.querySelectorAll(".ot--data .ot--option-row").forEach((el) => {
          el.classList.remove("active");
        });
        optionTable.querySelectorAll(".ot--images .ot--option-row").forEach((el) => {
          el.style.opacity = "1";
        });
        optionTable.querySelectorAll(`:scope .data-row.data-headers>div:not(.blank)`).forEach(el => {
          el.classList.remove('active');
        })
      });
    });
  }
}
customElements.define('option-table', OptionTable);

class LocalizationForm extends HTMLElement {
  constructor() {
    super();

    this.elements = {
      input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
      button: this.querySelector('button'),
      panel: this.querySelector('ul'),
    };
    
    this.elements.button.addEventListener('click', this.openSelector.bind(this));
    this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.querySelectorAll('a').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  hidePanel() {
    this.elements.button.setAttribute('aria-expanded', 'false');
    this.elements.panel.setAttribute('hidden', true);

    if (this.classList.contains('on-header')) {
      document.body.classList.remove('localization--open');
    }
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.hidePanel();
    // this.elements.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector('form');
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }

  openSelector() {
    // this.elements.button.focus();
    this.elements.panel.toggleAttribute('hidden');
    this.elements.button.setAttribute('aria-expanded', (this.elements.button.getAttribute('aria-expanded') === 'false').toString());

    if (this.classList.contains('on-header')) {
      document.body.classList.toggle('localization--open');
    }
  }

  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }
}
customElements.define('localization-form', LocalizationForm);

class Carousel extends HTMLElement {
  constructor() {
    super();

    this.options = JSON.parse(this.dataset.settings || {});

    window.addEventListener('beforeunload', this.onBeforeUnload.bind(this));

    if(this.options.delay) {
      setTimeout(() => {
        this.init();
      }, this.options.delay);
    } else {
      this.init();
    }
  }

  init() {
    this.carousel = new Flickity(this.querySelector('.carousel'), this.options);
    this.bindCarouselA11yListeners();
    this.syncCarouselCellAccessibility();

    // We trigger a resize so the component can re-align itself after loading
    this.carousel.resize();
  }

  onBeforeUnload() {
    this.removeCarouselA11yListeners();
    this.querySelectorAll('[data-a11y-orig-tabindex], [data-a11y-orig-href], [data-a11y-disabled-by-script]').forEach((el) => {
      this.restoreFocusableElement(el);
    });

    if(this.carousel) {
      this.carousel.destroy();
    }
  }

  get focusableSelector() {
    return 'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  }

  bindCarouselA11yListeners() {
    if (!this.carousel || typeof this.carousel.on !== 'function') return;

    this._onCarouselA11yUpdate = this.syncCarouselCellAccessibility.bind(this);
    this.carousel.on('change', this._onCarouselA11yUpdate);
    this.carousel.on('settle', this._onCarouselA11yUpdate);
  }

  removeCarouselA11yListeners() {
    if (!this.carousel || typeof this.carousel.off !== 'function' || !this._onCarouselA11yUpdate) return;

    this.carousel.off('change', this._onCarouselA11yUpdate);
    this.carousel.off('settle', this._onCarouselA11yUpdate);
    this._onCarouselA11yUpdate = null;
  }

  syncCarouselCellAccessibility() {
    if (!this.carousel || !this.carousel.element) return;

    this.carousel.element.querySelectorAll('.flickity-slider > *').forEach((cell) => {
      const isHidden = cell.getAttribute('aria-hidden') === 'true';
      if (isHidden) {
        cell.querySelectorAll(this.focusableSelector).forEach((el) => {
          this.hideFocusableElement(el);
        });
      } else {
        cell.querySelectorAll('[data-a11y-orig-tabindex], [data-a11y-orig-href], [data-a11y-disabled-by-script]').forEach((el) => {
          this.restoreFocusableElement(el);
        });
      }
    });
  }

  hideFocusableElement(element) {
    if (!element.hasAttribute('data-a11y-orig-tabindex')) {
      element.setAttribute('data-a11y-orig-tabindex', element.getAttribute('tabindex') || '');
    }
    element.setAttribute('tabindex', '-1');

    if ((element.tagName === 'A' || element.tagName === 'AREA') && element.hasAttribute('href') && !element.hasAttribute('data-a11y-orig-href')) {
      element.setAttribute('data-a11y-orig-href', element.getAttribute('href'));
      element.removeAttribute('href');
    }

    if (['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName) && !element.hasAttribute('disabled')) {
      element.setAttribute('data-a11y-disabled-by-script', 'true');
      element.setAttribute('disabled', 'disabled');
    }
  }

  restoreFocusableElement(element) {
    if (element.hasAttribute('data-a11y-orig-tabindex')) {
      const originalTabindex = element.getAttribute('data-a11y-orig-tabindex');
      if (originalTabindex === '') {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', originalTabindex);
      }
      element.removeAttribute('data-a11y-orig-tabindex');
    }

    if (element.hasAttribute('data-a11y-orig-href')) {
      element.setAttribute('href', element.getAttribute('data-a11y-orig-href'));
      element.removeAttribute('data-a11y-orig-href');
    }

    if (element.hasAttribute('data-a11y-disabled-by-script')) {
      element.removeAttribute('disabled');
      element.removeAttribute('data-a11y-disabled-by-script');
    }
  }
}

customElements.define('carousel-component', Carousel);

class PromoOverlay extends HTMLElement {
  constructor() {
    super();

    this.options = this.querySelector('script').innerText;
    this.promoOverlayShown = Cookies.get('promoOverlayShown');
    this.returningUser = Cookies.get('newuser');
    this.userScrolled = false;
    this.toggleButton = this.querySelector('.promo-overlay__toggle');
    this.toggleButton.addEventListener('click', this.close.bind(this));

    if( ! this.options.returningUsersOnly || (this.options.returningUsersOnly && this.returningUser == 'true')) {
      window.addEventListener('scroll', this.onScrollHandler.bind(this));
    }
  }

  onScrollHandler() {
    if(this.userScrolled && this.promoOverlayShown != 'true') {
      const timeout = setTimeout(() => {
        this.open();
        Cookies.set('promoOverlayShown', true, { expires: this.options.hideFor });
        this.promoOverlayShown = 'true';
      }, 3000);

      // clearTimeout(timeout);
    }
    this.userScrolled = true;
  }

  open() {
    this.setAttribute('open', 'open');
  }

  close() {
    Cookies.set('promoOverlayShown', true, { expires: this.options.hideFor });
    this.removeAttribute('open');
  }
}
customElements.define('promo-overlay', PromoOverlay);

class MilestoneTracker extends HTMLElement {
  // Thresholds count physical, non-gift items only (promotion-sync.js getQualifyingTotal). Payloads without a cart
  // total (e.g. /cart/add.js responses) return undefined so the caller fetches /cart.js instead of showing $0
  static qualifyingTotal(cart) {
    if (!cart || typeof cart.total_price !== 'number') return undefined;
    if (Array.isArray(cart.items) && window.promotionSync) return window.promotionSync.getQualifyingTotal(cart);
    return cart.total_price;
  }

  constructor() {
    super();

    this.data = JSON.parse(this.querySelector('script').innerHTML);
    this.milestones = this.data.milestones.sort((a, b) => a.minSpend - b.minSpend);
    this.cartTotal = parseInt(this.dataset.cartTotal);
    this.cartHasBundle = this.data.cartHasBundle;

    this.milestonesContainer = this.querySelector('.milestone-tracker__container');
    this.milestonesLoader = this.querySelector('.milestone-tracker__loader');
    this.progressBar = this.querySelector('.milestone-tracker__progress-bar');
    this.milestonesUl = this.querySelector('.milestones');
    
    this.isUpdating = false;

    this.initMilestones();

    ['cart:updated', 'ajaxProduct:added', 'promo:claimed'].forEach(event => {
      document.addEventListener(event, debounce((e) => this.executeMilestoneTrackerUpdate(e.detail.cart), 500));
    })
  }

  connectedCallback() {
    if (this.dataset.trackerType !== 'fresh') return;
    const script = this.querySelector('script');
    this.freshDataObserver = new MutationObserver(() => {
      const data = JSON.parse(script.textContent);
      if (data.cartHasBundle !== this.cartHasBundle) return;
      this.executeMilestoneTrackerUpdate(JSON.stringify({ total_price: data.cartTotal }));
    });
    this.freshDataObserver.observe(script, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    this.freshDataObserver?.disconnect();
    if(this.teardownFlickityResize) {
      this.teardownFlickityResize();
      this.teardownFlickityResize = null;
    }

    if(this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
    }
  }

  async executeMilestoneTrackerUpdate(cart) {
    if (this.isUpdating) {
      if (this.dataset.trackerType === 'fresh') this.pendingFreshCart = cart;
      return;
    }
    this.isUpdating = true;

    this.data = JSON.parse(this.querySelector('script').innerHTML);
    this.milestones = this.data.milestones.sort((a, b) => a.minSpend - b.minSpend);
    
    // Re-initialise the tracker if there was a bundle previously and it was removed
    if(this.cartHasBundle != this.data.cartHasBundle) {
      this.cartHasBundle = this.data.cartHasBundle;

      const dummyMiniCart = document.createElement('div');
      dummyMiniCart.innerHTML = JSON.parse(cart).sections['mini-cart'];

      if(this.flickity) {
        this.flickity.destroy();
      }
      
      this.querySelector('.milestone-tracker').innerHTML = dummyMiniCart.querySelector('.milestone-tracker').innerHTML;
      //this.initMilestones();
      
      if(this.flickity) {
        this.flickity.reloadCells();
      }

      this.isUpdating = false;
      return;
    }

    try {
      let newCartTotal = this.cartTotal;

      if (this.dataset.trackerType === 'fresh' && typeof this.data.cartTotal === 'number') {
        newCartTotal = this.data.cartTotal;
      } else if (cart) {
        newCartTotal = MilestoneTracker.qualifyingTotal(JSON.parse(cart));
      } else {
        const res = await fetch('/cart.js').then(r => r.json());
        newCartTotal = MilestoneTracker.qualifyingTotal(res);
      }

      if(typeof newCartTotal != 'number') {
        const res = await fetch('/cart.js').then(r => r.json());
        newCartTotal = MilestoneTracker.qualifyingTotal(res);
      }

      // Skip update if nothing changed
      if (newCartTotal === this.cartTotal && this.dataset.trackerType !== 'fresh') {
        return; // no need to re-render
      }

      this.cartTotal = newCartTotal;
      
      let nextMilestone = null,
          currentMilestone = null,
          currentMilestoneIndex = null,
          previousMilestone = { minSpend: 0 },
          finalMilestone = this.milestones[this.milestones.length - 1],
          allMilestonesComplete = this.cartTotal >= finalMilestone.minSpend;

      const milestoneTrackerType = this.dataset.trackerType,
            trackProgressType = this.dataset.trackProgress;

      this.milestones.forEach((milestone, index) => {
        const el = this.querySelector('#milestone-'.concat(milestone.id));
        let progress = 0;

        el.classList.add('milestone');
        el.id = `milestone-${ milestone.id }`;
        el.classList.remove('is-current');
        if (milestoneTrackerType === 'fresh') el.style.setProperty('--progress', '0%');

        if(this.cartTotal >= milestone.minSpend) {
          el.classList.add('is-complete');
          el.style.setProperty('--progress', '100%');
        } else if( ! currentMilestone) {
          el.classList.add('is-current');
          currentMilestone = milestone;
          currentMilestoneIndex = index;

          if(index > 0) {
            previousMilestone = this.milestones[index - 1];
            progress = ((this.cartTotal - previousMilestone.minSpend) / (milestone.minSpend - previousMilestone.minSpend));
          } else {
            progress = (this.cartTotal / milestone.minSpend);
          }

          el.style.setProperty('--progress', `${ progress * 100 }%`);
        }
        
        if( ! nextMilestone) {
          if(this.cartTotal < milestone.minSpend) {
            nextMilestone = {
              minSpend: milestone.minSpend,
              reward: milestone.reward
            };
          } else if(index + 1 < this.milestones.length) {
            if(this.cartTotal < this.milestones[index + 1].minSpend) {
              nextMilestone = {
                minSpend: this.milestones[index + 1].minSpend,
                reward: this.milestones[index + 1].reward
              }
            }
          }
        }
      });
      
      let footerText = window.languages.milestones.completeOne.replace('{reward}', '<span class="milestone-product">' + this.milestones[0].title + '</span>');

      if(this.milestones.length == 1) {
        this.milestonesContainer.classList.add('no-body');
      } else {
        footerText = window.languages.milestones.complete;
      }

      // Current milestone isn't present therefore the cart total is greater than all milestones min spend
      // i.e. all milestones complete
      if(allMilestonesComplete) {
        this.progressBar.style.setProperty('--progress', '100%');
        this.querySelector('#milestone-footer-label').innerHTML = footerText;
        this.querySelector('.milestone-tracker').classList.add('milestone-tracker--complete');
      } else {
        let progress = 0;

        this.querySelector('.milestone-tracker').classList.remove('milestone-tracker--complete');

        if( ! nextMilestone) {
          nextMilestone = finalMilestone;
        }

        if(milestoneTrackerType != 'classic') {
          nextMilestone = currentMilestone;
        }

        if(milestoneTrackerType != 'fresh') {
          if(trackProgressType == 'final_milestone') {
            progress = (this.cartTotal / finalMilestone.minSpend) * 100;
          } else {
            progress = (this.cartTotal / nextMilestone.minSpend) * 100;
          }
        }

        this.updateProgressBar(nextMilestone, progress);
      }

      // Update rewards list
      const milestoneRewards = this.querySelector('#milestone-rewards'),
            giftsUnlocked = this.dataset.trackerType === 'fresh' ? (this.closest('.mini-cart') || this).querySelector('[data-fresh-unlocked]') : document.querySelector('#gifts-unlocked');

      let qualifiedMilestones = 0,
          nextMilestoneIndex = currentMilestoneIndex !== null ? currentMilestoneIndex : this.milestones.length - 1;

      this.milestones.forEach((milestone, index) => {
        const milestoneElement = this.querySelector('#milestone-' . concat(milestone.id));

        milestoneElement.classList.remove('is-complete');

        if(this.cartTotal >= milestone.minSpend) {
          qualifiedMilestones++;
          milestoneElement.classList.add('is-complete');
        }
        
        if( ! milestoneRewards) return true;

        const rewards = milestoneRewards.querySelectorAll('li');

        rewards[index].classList.remove('reward--claimed');
        rewards[index].classList.remove('reward--qualified');
        rewards[index].classList.remove('reward--locked');

        if(this.cartTotal >= milestone.minSpend) { 
          if(milestone.claimed) {
            rewards[index].classList.add('reward--claimed');
          } else {
            rewards[index].classList.add('reward--qualified');
          }
        } else {
          rewards[index].classList.add('reward--locked');
        }
      });

      this.updateFreshAccessibleStates();

      if (milestoneTrackerType === 'fresh' && giftsUnlocked) {
        this.updateFreshGiftCount(giftsUnlocked);
      }

      if(this.flickity) {
        this.flickity.reloadCells();
        this.flickity.resize();
        this.flickity.select(nextMilestoneIndex, false, false);
      }
    } finally {
      this.isUpdating = false;
      if (this.pendingFreshCart !== undefined) {
        const pendingCart = this.pendingFreshCart;
        this.pendingFreshCart = undefined;
        this.executeMilestoneTrackerUpdate(pendingCart);
      }
    }
  }

  async initMilestones() {

    let nextMilestone = null,
        currentMilestone = null,
        currentMilestoneIndex = null,
        previousMilestone = { minSpend: 0 },
        finalMilestone = this.milestones[this.milestones.length - 1],
        allMilestonesComplete = this.cartTotal >= finalMilestone.minSpend;

    const milestoneTrackerType = this.dataset.trackerType,
          trackProgressType = this.dataset.trackProgress,
          milestoneElementType = milestoneTrackerType == 'classic' ? 'div' : 'li';

    this.milestones.forEach((milestone, index) => {
      const el = document.createElement(milestoneElementType);
      let progress = 0;

      el.classList.add('milestone');
      el.id = `milestone-${ milestone.id }`;

      if(this.cartTotal >= milestone.minSpend) {
        el.classList.add('is-complete');
        el.style.setProperty('--progress', '100%');
      } else if( ! currentMilestone) {
        el.classList.add('is-current');
        currentMilestone = milestone;
        currentMilestoneIndex = index;

        if(index > 0) {
          previousMilestone = this.milestones[index - 1];
          progress = ((this.cartTotal - previousMilestone.minSpend) / (milestone.minSpend - previousMilestone.minSpend));
        } else {
          progress = (this.cartTotal / milestone.minSpend);
        }

        el.style.setProperty('--progress', `${ progress * 100 }%`);
      }

      if(milestoneTrackerType == 'classic') {
        el.innerHTML = '<span class="icon icon-checkmark-circle"></span><span class="icon icon-lock"></span>';
        el.innerHTML += `<span class="milestone__title">${ milestone.title }</span>`;
      } else if (milestoneTrackerType === 'fresh') {
        const node = document.createElement('span');
        node.className = 'milestone__node';
        node.setAttribute('aria-hidden', 'true');
        const symbol = document.createElement('span');
        symbol.className = 'icon icon-lock milestone__symbol';
        const tick = document.createElement('span');
        tick.className = 'icon icon-checkmark milestone__tick';
        node.append(symbol, tick);
        const label = document.createElement('span');
        label.className = 'milestone__label';
        label.textContent = milestone.id === 'free-shipping' ? 'FREE Shipping' : milestone.title;
        const amount = document.createElement('span');
        amount.className = 'milestone__amount';
        amount.textContent = this.formatFreshAmount(milestone.minSpend);
        el.append(node, label, amount);
      } else {
        el.innerHTML = `<span class="icon icon-${ milestone.icon }"></span>`;
      }
      
      this.milestonesUl.append(el);

      if( ! nextMilestone) {
        if(this.cartTotal < milestone.minSpend) {
          nextMilestone = {
            minSpend: milestone.minSpend,
            reward: milestone.reward
          };
        } else if(index + 1 < this.milestones.length) {
          if(this.cartTotal < this.milestones[index + 1].minSpend) {
            nextMilestone = {
              minSpend: this.milestones[index + 1].minSpend,
              reward: this.milestones[index + 1].reward
            }
          }
        }
      }
    });
    
    let footerText = window.languages.milestones.completeOne.replace('{reward}', '<span class="milestone-product">' + this.milestones[0].title + '</span>');

    if(this.milestones.length == 1) {
      this.milestonesContainer.classList.add('no-body');
    } else {
      footerText = window.languages.milestones.complete;
    }
    
    // Current milestone isn't present therefore the cart total is greater than all milestones min spend
    // i.e. all milestones complete
    if(allMilestonesComplete) {
      this.progressBar.style.setProperty('--progress', '100%');
      this.querySelector('#milestone-footer-label').innerHTML = footerText;
      this.querySelector('.milestone-tracker').classList.add('milestone-tracker--complete');
    } else {
      let progress = 0;

      this.querySelector('.milestone-tracker').classList.remove('milestone-tracker--complete');

      if( ! nextMilestone) {
        nextMilestone = finalMilestone;
      }

      if(milestoneTrackerType != 'classic') {
        nextMilestone = currentMilestone;
      }

      if(milestoneTrackerType != 'fresh') {
        if(trackProgressType == 'final_milestone') {
          progress = (this.cartTotal / finalMilestone.minSpend) * 100;
        } else {
          progress = (this.cartTotal / nextMilestone.minSpend) * 100;
        }
      }

      this.updateProgressBar(nextMilestone, progress);
    }

    // Construct rewards list
    const milestoneRewards = this.querySelector('#milestone-rewards'),
          giftsUnlocked = this.dataset.trackerType === 'fresh' ? (this.closest('.mini-cart') || this).querySelector('[data-fresh-unlocked]') : document.querySelector('#gifts-unlocked');

    let qualifiedMilestones = 0,
        nextMilestoneIndex = currentMilestoneIndex !== null ? currentMilestoneIndex : this.milestones.length - 1;

    this.milestones.forEach((milestone, index) => {
      if(this.cartTotal >= milestone.minSpend) {
        if( ! this.data.cartHasBundle || milestone.id == 'free-shipping' || milestone.isGift === false) {
          qualifiedMilestones++;
        }
      }
      
      if( ! milestoneRewards) return true;

      const reward = document.createElement('li');
      reward.id = 'milestone-'.concat(milestone.id, '--reward');

      reward.classList.add('reward');

      if (milestoneTrackerType === 'fresh') {
        const unlocked = this.cartTotal >= milestone.minSpend;
        reward.classList.add(unlocked ? (milestone.claimed ? 'reward--claimed' : 'reward--qualified') : 'reward--locked');
        const status = document.createElement('span');
        status.className = 'fresh-rewards__status';
        status.setAttribute('aria-hidden', 'true');
        const name = document.createElement('span');
        name.className = 'fresh-rewards__name';
        const rewardTitles = milestone.isGift && Array.isArray(milestone.reward)
          ? [...new Set(milestone.reward.map(reward => reward.title).filter(Boolean))]
          : [];
        name.textContent = rewardTitles.length ? rewardTitles.join(' or ') : milestone.title;
        const amount = document.createElement('span');
        amount.className = 'fresh-rewards__amount';
        amount.textContent = this.formatFreshAmount(milestone.minSpend);
        reward.append(status, name, amount);
        milestoneRewards.append(reward);
        return;
      }

      if(this.cartTotal >= milestone.minSpend) { 
        if(milestone.claimed) {
          reward.classList.add('reward--claimed');
          reward.innerText = window.languages.promotions.claimed.replace('{{reward}}', ((milestone.reward.map(i => i)).filter(i => i.claimed)[0].title));
        } else {
          reward.classList.add('reward--qualified', `reward--${ milestone.icon }`);
          reward.innerText = window.languages.promotions.qualified.replace('{{reward}}', ((milestone.reward.map(i => i)).map(i => i.title)).join(' or '));
        }
      } else {
        reward.classList.add('reward--locked');
        //reward.innerText = ((milestone.reward.map(i => i)).map(i => i.title)).join(' or ');
        reward.innerText = milestone.title.toLowerCase().includes('free') ? milestone.title : 'Free '.concat(milestone.title);
      }

      milestoneRewards.append(reward);
    });

    if (giftsUnlocked) {
      if (milestoneTrackerType === 'fresh') this.updateFreshGiftCount(giftsUnlocked);
      else giftsUnlocked.innerText = `${ qualifiedMilestones } of ${ this.milestones.length }`;
    }

    this.updateFreshAccessibleStates();

    this.milestonesContainer.classList.remove('hidden');
    this.milestonesLoader.classList.add('hidden');

    if (milestoneTrackerType === 'fresh') this.initFreshDiagram();

    let applyResize = () => {};

    if(milestoneTrackerType == 'classic' && this.milestones.length > 1) {
      this.flickity = new Flickity(this.milestonesUl, {
        pageDots: false,
        draggable: true,
        freeScroll: false,
        wrapAround: false,
        cellAlign: 'left',
        contain: true,
        arrowShape: {
          x0: 10,
          x1: 60, y1: 50,
          x2: 70, y2: 40,
          x3: 30
        },
        initialIndex: nextMilestoneIndex
      });

      applyResize = () => {
        if( ! this.flickity) return;

        const selectedIndex = this.flickity.selectedIndex;

        this.flickity.reloadCells();
        this.flickity.resize();
        this.flickity.select(selectedIndex, false, true);
      };

      if (typeof ResizeObserver != 'undefined') {
        let resizeDebounce = null;
        let resizePending = false;

        const scheduleApply = () => {
          clearTimeout(resizeDebounce);
          resizeDebounce = setTimeout(() => {
            if( ! this.flickity) return;

            if(this.flickity.isAnimating || this.flickity.isDragging) {
              resizePending = true;
              return;
            }

            applyResize();
          }, 50);
        };

        const resizeObserver = new ResizeObserver(scheduleApply);
        resizeObserver.observe(this.milestonesUl);

        this.milestonesUl.addEventListener('transitionend', scheduleApply);

        this.flickity.on('settle', () => {
          if( ! resizePending) return;

          resizePending = false;
          applyResize();
        });

        this.teardownFlickityResize = () => {
          clearTimeout(resizeDebounce);
          resizeObserver.disconnect();
          this.milestonesUl.removeEventListener('transitionend', scheduleApply);
        };
      }
    }

    // The tracker markup always carries milestone-tracker--no-transition (see milestone-tracker.liquid)
    // to suppress the first-paint margin transition regardless of tracker type; clear it here for every type.
    const trackerStylesheet = document.querySelector('link[href*="component-milestone-tracker"]');

    const enableTransitions = () => {
      const trackerElement = this.querySelector('.milestone-tracker');
      if(trackerElement) trackerElement.classList.remove('milestone-tracker--no-transition');
    };

    if(trackerStylesheet && ! trackerStylesheet.sheet) {
      trackerStylesheet.addEventListener('load', () => {
        applyResize();
        enableTransitions();
      }, { once: true });
    } else {
      enableTransitions();
    }

  }

  formatFreshAmount(amount) {
    return theme.Currency.formatMoney(amount, theme.shopSettings.moneyFormat)
      .replace(/<[^>]*>/g, '').replace(/([.,])00(?=\D*$)/, '');
  }

  updateFreshAccessibleStates() {
    if (this.dataset.trackerType !== 'fresh') return;
    this.milestones.forEach(milestone => {
      const unlocked = this.cartTotal >= milestone.minSpend;
      const state = !unlocked ? 'Locked' : milestone.isGift && milestone.claimed ? 'Added to cart' : 'Unlocked';
      ['#milestone-'.concat(milestone.id), '#milestone-'.concat(milestone.id, '--reward')].forEach(selector => {
        const node = this.querySelector(selector);
        if (!node) return;
        let description = node.querySelector('[data-fresh-accessible-state]');
        if (!description) {
          description = document.createElement('span');
          description.className = selector.endsWith('--reward') ? 'fresh-rewards__state' : 'visually-hidden';
          description.setAttribute('data-fresh-accessible-state', '');
          node.append(description);
        }
        description.textContent = state;
      });
    });
  }

  updateFreshGiftCount(counter) {
    const unlocked = this.milestones.filter(milestone => this.cartTotal >= milestone.minSpend);
    counter.textContent = `${unlocked.length} of ${this.milestones.length} Gifts Unlocked`;
    counter.hidden = this.milestones.length === 0;
  }

  initFreshDiagram() {
    const button = this.querySelector('.milestone-tracker__scroll');
    if (!button) return;
    const rail = this.milestonesUl;
    const scrollable = this.milestones.length > 3;
    this.querySelector('.milestone-tracker').classList.toggle('milestone-tracker--scrollable', scrollable);
    button.hidden = !scrollable;
    const update = () => {
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      button.firstElementChild.textContent = atEnd ? '‹' : '›';
      button.setAttribute('aria-label', atEnd ? 'Back to first reward milestones' : 'Show more reward milestones');
    };
    button.addEventListener('click', () => {
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      rail.scrollTo({ left: atEnd ? 0 : rail.scrollLeft + rail.clientWidth * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
    rail.addEventListener('scroll', update, { passive: true });
    update();
  }

  updateProgressBar(nextMilestone, progress) {
    let nextMilestoneReward = '';

    nextMilestone.reward.forEach((reward, index) => {
      if(nextMilestoneReward.indexOf(reward.title) >= 0) return true;

      // nextMilestoneReward += reward.url ? `<a href="${ reward.url }">${ reward.title }</a>` : reward.title;
      nextMilestoneReward += reward.title;

      if(nextMilestone.reward[index + 1] && nextMilestoneReward.indexOf(nextMilestone.reward[index + 1].title) < 0) {
        nextMilestoneReward += ' or ';
      }
    });
    
    const nextMilestoneAmount = theme.shopSettings.moneyFormat.replace('{{amount}}', ((nextMilestone.minSpend - this.cartTotal) / 100).toFixed(2).replace(/\.00$/, '')),
          nextMilestoneText = window.languages.milestones.next.replace('{{amount}}', nextMilestoneAmount).replace('Free {{reward}}', '<span class="milestone-product">'.concat(nextMilestoneReward, '</span>'));
          
    this.querySelector('#milestone-footer-label').innerHTML = nextMilestoneText;
    this.progressBar.style.setProperty('--progress', progress + '%');
  }
}
customElements.define('milestone-tracker', MilestoneTracker);

class CartUpsell extends HTMLElement {
  constructor() {
    super();
  }
  
  connectedCallback() {
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);
    
    this.init();
  }

  init() {
    this.style.display = 'block';
  }
}
customElements.define('cart-upsell', CartUpsell);

// Custom dropdown (details/summary) variant picker behavior
document.addEventListener('change', function(event) {
  const customSelect = event.target.closest('details.custom-select');
  if (!customSelect) return;

  const checkedOption = customSelect.querySelector('.button-input__input:checked + .custom-select__option');
  const triggerValue = customSelect.querySelector('.custom-select__value');
  if (checkedOption && triggerValue) {
    const triggerInfo = triggerValue.querySelector('.variant-info');
    const optionInfo = checkedOption.querySelector('.variant-info');
    if (triggerInfo && optionInfo) triggerInfo.innerHTML = optionInfo.innerHTML;

    const existingIcon = triggerValue.querySelector('.variant-icon');
    if (existingIcon) existingIcon.remove();
    const newIcon = checkedOption.querySelector('.variant-icon');
    if (newIcon) triggerValue.insertBefore(newIcon.cloneNode(true), triggerValue.firstChild);

    const existingBadge = triggerValue.querySelector('.variant-badge');
    if (existingBadge) existingBadge.remove();
    const newBadge = checkedOption.querySelector('.variant-badge');
    if (newBadge) triggerValue.appendChild(newBadge.cloneNode(true));
  }

  customSelect.open = false;
});

document.addEventListener('click', function(event) {
  document.querySelectorAll('details.custom-select[open]').forEach(function(el) {
    if (!el.contains(event.target)) el.open = false;
  });
});

// Track which surface a product card was clicked from, for ATC ref attribution
document.addEventListener('click', function(e) {
  const card = e.target.closest('[data-atc-ref]');
  if (!card) return;
  if (e.target.closest('a[href], quick-view-button')) {
    try { sessionStorage.setItem('atc_ref', card.dataset.atcRef); } catch (e) {}
  }
});
